'use client';
import React, { useEffect, useState } from 'react';
import { useGed } from '@/lib/store';
import { fmtDate, GED_ROLES, ORG_TIERS, ROLES_ORG, tierOfUser, jourIso, aujourdhuiIso } from '@/lib/meta';
import { libelleProfil } from '@/lib/profils-comptes';
/* Une SEULE implémentation du rattachement, partagée avec l'écran des parcours
   d'intégration — cf. l'en-tête de `RattacherCompte`. */
import { RattacherCompte } from '@/components/RattacherCompte';
/* UNE SEULE implémentation des pièces du dossier, partagée avec le parcours
   d'intégration — cf. l'en-tête de `PiecesDossier`. */
import { BlocPieces } from '@/components/PiecesDossier';
import { DetacherCompte } from '@/components/DetacherCompte';
import { InviterCompte } from '@/components/InviterCompte';
import { SelecteurCouleur } from '@/components/SelecteurCouleur';
import { couleursPrises, pastilleDe, teinteEffective } from '@/lib/couleurs-personne';
import { peutLirePlanning } from '@/lib/acces-planning';
import { VOILE, COUCHES, I, card, inputStyle, Modale, SelecteurPastilles, ConfirmButton } from '@/components/ui';
import { useSortieFenetre, AlerteSortie } from '@/shared/fenetreModale';
import { ChampTelephone } from '@/shared/ChampTelephone';
import { useVerrouScroll } from '@/shared/verrouScroll';
import type { GedUser, RhContrat, RhIntegrationPiece } from '@/lib/types';
/* ── LE DÉCOUPAGE EN ONGLETS VIT DANS UN MODULE PUR ────────────────────────
   `Brouillon`, `BROUILLON_VIDE` et `CHAMPS_ONGLET` en sont sortis le
   06/10/2026 : ce sont les deux endroits où une erreur ne se VOIT pas (un champ
   rattaché à aucun onglet n'est jamais envoyé et la route répond 200), et ils
   se vérifient par le calcul dès qu'ils sont lisibles hors navigateur. La suite
   `backend/tests/front-dossier-rh-onglets.test.js` les relit. */
import {
  type Brouillon, type Onglet, type OngletEditable,
  BROUILLON_VIDE, CHAMPS_ONGLET, ONGLETS_DOSSIER, estEditable,
} from '@/lib/onglets-dossier-rh';
import { initials } from './commun';
import { nomDe } from '@/lib/nom';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { PiedActions } from '@/shared/PiedActions';
import { SignaturePersonne } from '@/components/SignaturePersonne';
/**
 * LE DOSSIER RH D'UNE PERSONNE — et, depuis le 06/10/2026, le SEUL endroit où
 * il s'écrit.
 *
 * ⚠️ L'ORGANIGRAMME N'OUVRE PLUS CE DOSSIER. Il ouvrait exactement ce
 * composant, éditable, par un clic sur une carte : on modifiait donc le dossier
 * depuis l'écran de la STRUCTURE, et deux portes menaient à la même écriture.
 * Il ouvre maintenant `ResumePersonne`, en lecture seule, qui renvoie ici.
 * Cet écran s'ouvre depuis « Dossiers RH » (barre latérale) ou par l'adresse
 * `/dossiers-rh` portant `selectedPersonne`.
 *
 * Le découpage en six onglets et la règle d'enregistrement qui va avec sont
 * expliqués juste au-dessus du composant ; la table des champs vit dans
 * `@/lib/onglets-dossier-rh` pour être vérifiable hors navigateur.
 *
 * POURQUOI UN SEUL ÉCRAN pour ce qui vient de deux modules. Ce que le
 * laboratoire appelle « le dossier de Untel » est éclaté dans le produit :
 * l'identité et les compétences vivent dans la Qualité, le contrat et les dates
 * dans le RH, le cycle de vie du compte dans le module Site. Répondre « qui est
 * cette personne, que porte-t-elle, peut-elle encore signer » obligeait à
 * ouvrir trois écrans et à recouper soi-même. Le dossier les rassemble ; et
 * pour ce qui n'est pas à lui — habilitations, absences, pièces du parcours,
 * journal — il RASSEMBLE EN LECTURE et nomme l'écran qui écrit : chaque donnée
 * reste écrite là où elle est née.
 *
 * CE QUI EST CACHÉ SANS LE RÔLE `rh`, et ce n'est pas une politesse : matricule,
 * badge, téléphone, dates d'entrée et de sortie, contrats. La séparation est
 * celle de la spec (D19) — le RQ n'y accède que s'il porte AUSSI le rôle RH, et
 * il y accède à ce titre.
 */

const SECTION: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.07em', color: 'var(--text-muted)', margin: '0 0 9px',
};

/**
 * Libellé de l'ÉTAT DU COMPTE DE CONNEXION — celui que le serveur calcule
 * contre `up_users`, et non le miroir `statutCompte` posé à la création.
 *
 * ⚠️ `invite` et `expiree` sont les deux états qui manquaient, et ce sont
 * exactement ceux qui demandent un geste : l'un dit d'attendre, l'autre de
 * relancer. Les confondre avec « actif » faisait croire la personne joignable.
 */
const ETAT_COMPTE: Record<string, { label: string; bg: string; fg: string }> = {
  actif: { label: 'Compte actif', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  invite: { label: 'Invitation en attente', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  expiree: { label: 'Invitation expirée', bg: 'var(--red-50)', fg: 'var(--red-600)' },
  suspendu: { label: 'Compte suspendu', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  archive: { label: 'Compte archivé', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

/** Libellé du statut de DOSSIER (distinct du cycle de vie du compte). */
const STATUT_DOSSIER: Record<string, { label: string; bg: string; fg: string }> = {
  actif: { label: 'En service', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  suspendu: { label: 'Suspendu', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  sorti: { label: 'Sorti des effectifs', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

/** Taille lisible d'une pièce jointe. */
const poids = (o: number) => (o >= 1048576 ? `${(o / 1048576).toFixed(1).replace('.', ',')} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`);

const MAIL_OK = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

/** Grille LIBELLÉ | VALEUR — deux colonnes alignées, utilisée par « Rôles &
 *  responsabilités » (02/10/2026 : « affichage trop compliqué »). Une rangée = un
 *  libellé en face de son résultat, sans imbrication ni sous-bloc. */
const GRILLE: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(140px, 200px) 1fr', columnGap: 16, alignItems: 'start' };
function Rang({ label, children }: { label: string; children: React.ReactNode }) {
  const bord = '1px solid var(--border-default)';
  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '9px 0', borderTop: bord }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-strong)', padding: '7px 0', borderTop: bord, minWidth: 0, lineHeight: 1.5 }}>{children}</div>
    </>
  );
}

/** Découpage lisible d'un RIB : banque · agence · compte · clé. */
const ribLisible = (r: string) => (r && r.length === 20
  ? `${r.slice(0, 2)} ${r.slice(2, 5)} ${r.slice(5, 18)} ${r.slice(18)}` : r);

/** Les types de contrat proposés à la saisie, code stocké → intitulé lu.
 *
 *  ⚠️ Le backend ne tient AUCUNE liste fermée : `contratType` est une chaîne
 *  libre (`saveContrat`, api/ged/controllers/rh.ts) et le schéma Strapi la
 *  déclare en `string`. Cette liste est donc le seul endroit qui la définit —
 *  un type absent d'ici n'est saisissable nulle part.
 *
 *  ⚠️ L'intitulé complet est porté ICI et non dans une info-bulle : « CIVP »
 *  ne se devine pas, et ce qui ne s'affiche qu'au survol n'existe pas pour qui
 *  saisit au clavier. Le CIVP (contrat d'initiation à la vie professionnelle)
 *  est celui des trois dernières arrivées — Ons, Syrine et Noureddine. */
const TYPES_CONTRAT: Array<{ valeur: string; intitule: string }> = [
  { valeur: 'cdi', intitule: 'CDI — contrat à durée indéterminée' },
  { valeur: 'cdd', intitule: 'CDD — contrat à durée déterminée' },
  { valeur: 'civp', intitule: 'CIVP — contrat d’initiation à la vie professionnelle' },
  { valeur: 'stage', intitule: 'Stage' },
  { valeur: 'apprentissage', intitule: 'Apprentissage' },
  { valeur: 'interim', intitule: 'Intérim' },
];

/** Ligne d'édition : même gabarit que `Ligne`, pour que rien ne saute d'un
 *  pixel entre la lecture et la saisie. */
function LigneSaisie({ label, children, aide }: {
  label: string; children: React.ReactNode; aide?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '132px 1fr', gap: 10, padding: '4px 0', alignItems: aide ? 'start' : 'center' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)', paddingTop: aide ? 7 : 0 }}>{label}</span>
      {/* ⚠️ L'AIDE EST SOUS SON CHAMP, jamais dans un pavé de fin de bloc.
          Les quatre réserves de la rubrique Administratif — date de sortie,
          badge, matricule de paie, RIB — vivaient dans un seul paragraphe de
          six phrases, sous neuf champs d'apparence identique : il fallait
          relire tout le pavé pour savoir laquelle parlait du champ qu'on
          était en train de remplir, et rien ne rattachait « la clé de
          contrôle » au RIB plutôt qu'au badge. Une réserve qu'on ne lit qu'en
          la cherchant ne protège personne. */}
      {aide ? (
        <div>
          {children}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>{aide}</div>
        </div>
      ) : children}
    </div>
  );
}

/**
 * « Ceci ne se modifie pas ici, et voilà où. »
 *
 * La fiche est devenue éditable le 01/09/2026 pour tout ce qui appartient au
 * dossier de la personne. Quatre blocs restent en lecture, chacun pour une
 * raison — mais un bloc muet au milieu de blocs éditables se lit « fonction
 * manquante », pas « décision ». On nomme donc l'endroit à chaque fois.
 */
function Ailleurs({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '9px 0 0', lineHeight: 1.5 }}>
      {children}
    </p>
  );
}

function Ligne({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '132px 1fr', gap: 10, padding: '5px 0', alignItems: 'baseline' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{children}</span>
    </div>
  );
}

const vide = <span style={{ color: 'var(--text-faint)' }}>—</span>;
/* `fmtDate` de la maison (24/08/2026) : ce helper écrivait 24/08/2026 avec des
   barres, quand les 40 autres fichiers écrivent 24.08.2026. */
const jour = (d?: string | null) => (d ? fmtDate(d) : null);

/**
 * CE QU'UN CHOIX VA DÉPLACER — l'avertissement qui survit au passage en liste.
 *
 * Un processus n'a qu'UN pilote et qu'UN suppléant (arbitrage RQ) : cocher
 * quelqu'un ici RETIRE l'autre, sans rien demander. L'ancienne interface le
 * disait en affichant toutes les options et en teintant d'ambre celles déjà
 * tenues, avec le nom du titulaire — mais elle ne pouvait le faire qu'en
 * affichant les douze processus en permanence, c'est-à-dire en produisant très
 * exactement l'entassement signalé le 11/09.
 *
 * L'avertissement est donc redonné ICI, et seulement pour ce qui est RÉELLEMENT
 * choisi : une ligne quand il y a quelque chose à dire, rien sinon. On perd la
 * pré-visualisation (« si je cochais celui-ci, je déplacerais Asma »), on garde
 * le fait (« en enregistrant, je déplace Asma »). C'est le second qui compte :
 * il porte sur un geste qu'on est en train de faire, pas sur douze qu'on ne
 * fera pas.
 */
function DeplacementsProcessus({ titulaires, verbe }: {
  titulaires: { nom: string; qui: string }[];
  verbe: 'piloté' | 'suppléé';
}) {
  if (!titulaires.length) return null;
  return (
    <div style={{ marginTop: 5, fontSize: 11.5, lineHeight: 1.5, color: 'var(--amber-600)' }}>
      <strong>À l’enregistrement, ce choix déplace :</strong>{' '}
      {titulaires.map((x) => `${x.nom} (actuellement ${verbe} par ${x.qui || 'quelqu’un d’autre'})`).join(' · ')}.
    </div>
  );
}


/**
 * CHANGER L'IDENTIFIANT DE CONNEXION (RQ ou RH, 02/10/2026).
 *
 * ⚠️ Ce n'est PAS le numéro de personne (« P-0007 ») : celui-ci ne change jamais,
 * c'est lui que le journal signe. L'identifiant est ce qu'on TAPE pour se
 * connecter ; le changer oblige la personne à saisir le nouveau la fois
 * suivante. La fenêtre le dit avant de valider.
 */
function ChangerIdentifiant({ orgId, actuel, onFait }: { orgId: string; actuel: string; onFait: () => void }) {
  const { changerIdentifiantCompte } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [valeur, setValeur] = useState('');
  const [busy, setBusy] = useState(false);
  const propre = valeur.trim().toLowerCase();
  const valide = /^[a-z0-9][a-z0-9._-]{2,39}$/.test(propre) && propre !== actuel;
  const valider = async () => {
    setBusy(true);
    const ok = await changerIdentifiantCompte(orgId, propre);
    setBusy(false);
    if (ok) { setOuvert(false); onFait(); }
  };
  return (
    <>
      <Bouton taille="sm" type="button" onClick={() => { setValeur(actuel); setOuvert(true); }}>
        <I n="pen" s={11} />Modifier
      </Bouton>
      {ouvert && (
        <Modale titre="Modifier l’identifiant de connexion" largeur={520} onClose={() => setOuvert(false)}
          pied={<>
            <Bouton taille="sm" disabled={busy} onClick={() => setOuvert(false)}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" disabled={!valide || busy} onClick={() => void valider()}>
              <I n="check" s={12} />{busy ? 'Enregistrement…' : 'Changer l’identifiant'}
            </Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 8 }}>
            <label htmlFor="nouvel-identifiant" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Nouvel identifiant</label>
            <input id="nouvel-identifiant" value={valeur} autoFocus autoComplete="off" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
              onChange={(e) => setValeur(e.target.value)} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Minuscules, chiffres, point, tiret ou tiret bas, sans accent ni espace (3 à 40 caractères, ex. prenom.nom). La personne devra saisir ce nouvel
              identifiant à sa prochaine connexion ; son mot de passe et ses droits ne changent pas, et son
              numéro de personne non plus.
            </span>
          </div>
        </Modale>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LE DOSSIER RH EST LE HUB DE LA PERSONNE — six onglets (RQ, 06/10/2026)
 *
 * ── La demande, telle qu'elle a été posée ──────────────────────────────────
 *
 * « Le dossier RH contient tout tout tout. Quand je clique sur l'organigramme,
 * je veux voir un résumé non modifiable. Pour le modifier, il faut aller sur
 * le dossier RH. Le dossier RH, lui, je voudrais qu'il soit plus user friendly
 * avec plusieurs onglets qui regroupent les infos par intérêt et avec une
 * modification intuitive des informations. C'est à partir du dossier RH qu'on
 * gère tout ce qui est relié à la personne. »
 *
 * ── Ce que ça remplace, et le défaut qui l'a déclenché ─────────────────────
 *
 * La fiche était UNE page de sept blocs en deux colonnes, dans une fenêtre qui
 * défile, avec UN bouton « Modifier la fiche » et UN « Enregistrer la fiche »
 * posé dans l'EN-TÊTE. Deux conséquences mesurées :
 *
 *  · on cherchait « Enregistrer » en bas, là où la convention §5 le met et où
 *    tous les autres formulaires du produit le posent — il était en haut ;
 *  · un seul brouillon couvrait les sept blocs : ouvrir la modification pour
 *    corriger un numéro de badge mettait AUSSI en saisie le nom, le
 *    rattachement et les pilotages, et « Enregistrer » renvoyait les trente
 *    champs au serveur, dont vingt-neuf inchangés.
 *
 * ── Les deux décisions qui gouvernent ce fichier ───────────────────────────
 *
 * 1. SIX ONGLETS PAR INTÉRÊT, et non par origine technique. On ouvre le
 *    dossier avec une question en tête — « quel est son contrat ? », « que
 *    sait-elle faire ? », « combien de congés lui reste-t-il ? » — et chaque
 *    question a son onglet. Trois sont modifiables ; trois ne font que
 *    RASSEMBLER ce qui s'écrit ailleurs (habilitations, absences, pièces du
 *    parcours), fidèlement à la règle d'origine de cette fiche : elle réunit
 *    en lecture, chaque donnée reste écrite là où elle est née.
 *
 * 2. L'ÉDITION EST PAR ONGLET, avec son pied COLLÉ EN BAS.
 *
 *    ⚠️ Et l'enregistrement n'envoie QUE les champs de l'onglet ouvert
 *    (`CHAMPS_ONGLET`). Ce n'est pas une économie de réseau, c'est une
 *    garantie : la route `POST ged/rh/personnel/:orgId` est partielle — elle
 *    n'écrit un champ que s'il est présent dans le corps (`if (b.x !== undefined)`).
 *    Un onglet ne peut donc PAS écraser ce qu'un autre porte, même si son
 *    brouillon a été rempli depuis une lecture périmée. L'ancien envoi global
 *    n'avait pas cette propriété.
 *
 *    ⚠️ UN SEUL ONGLET EN SAISIE À LA FOIS, et les autres se verrouillent
 *    pendant ce temps. Autoriser le changement d'onglet aurait demandé de
 *    porter plusieurs brouillons sales en parallèle, puis de décider ce que
 *    « Enregistrer » fait des autres — c'est-à-dire de recréer le défaut qu'on
 *    vient de retirer. Le verrou DIT pourquoi (`title`), il ne se contente pas
 *    de griser.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Libellé d'un statut d'absence — le vocabulaire du registre des congés. */
const STATUT_ABSENCE: Record<string, { label: string; fg: string }> = {
  demandee: { label: 'demandée', fg: 'var(--amber-600)' },
  validee: { label: 'validée', fg: 'var(--green-600)' },
  refusee: { label: 'refusée', fg: 'var(--red-600)' },
  annulee: { label: 'annulée', fg: 'var(--text-muted)' },
};

export function DossierPersonnel({ orgId, onClose, onEnregistre }: {
  orgId: string;
  onClose: () => void;
  /* ⚠️ PLUS DE `onTransfert` (06/10/2026). Le dossier portait un bouton
     « Transférer ses responsabilités » dont l'appelant n'avait rien à faire
     d'utile : le panneau de transfert vit dans l'organigramme, si bien que
     « Dossiers RH » fermait le dossier, affichait un message et renvoyait à
     l'arbre — où il fallait recliquer la personne. Le geste est désormais sur le
     résumé de l'organigramme (`ResumePersonne`), et le dossier se contente de
     dire où il se trouve. Garder la propriété « au cas où » aurait laissé un
     point d'accroche pour remettre ce détour. */
  /** Prévient l'appelant qu'un enregistrement a réussi. */
  onEnregistre?: () => void;
}) {
  const {
    settings, docs, session, rhAcces, rhPersonnel,
    rhAbsences, rhSoldes, rhIntegrations, rhJournal,
    changerStatutPersonne, chargerContrats, majPersonnelRh, enregistrerContratId,
    joindrePieceContrat, retirerPieceContrat, ouvrirPieceContrat,
    deposerPiece, consulterPiece, supprimerPiece, chargerPiecesPersonne,
    loadRh, loadRhGestion, loadJournalRh, showToast, openSettings, naviguerVers, ouvrirDepuis,
  } = useGed();

  const [contrats, setContrats] = useState<RhContrat[] | null>(null);
  const [busy, setBusy] = useState(false);
  /** L'onglet affiché. L'identité d'abord : c'est « qui est-ce ? ». */
  const [onglet, setOnglet] = useState<Onglet>('identite');
  /** ⚠️ L'ÉDITION APPARTIENT À UN ONGLET, pas à la fenêtre (06/10/2026) :
   *  `null` = tout est en lecture. Un booléen aurait laissé une saisie ouverte
   *  survivre à un changement d'onglet, et « Enregistrer » aurait alors porté
   *  sur des champs qui n'étaient plus à l'écran. */
  const [enSaisie, setEnSaisie] = useState<OngletEditable | null>(null);
  /** Vrai quand la modification a été ouverte PAR le rond de couleur : la
   *  palette se déplie alors d'elle-même (cf. `ouvrirEdition`). */
  const [paletteALOuverture, setPaletteALOuverture] = useState(false);
  /** Le contrat en cours de saisie. `null` = aucun formulaire ouvert. */
  const [contratEdite, setContratEdite] = useState<{
    documentId: string; contratType: string; dateDebut: string; dateFin: string;
    regimeHebdo: string; quotite: string; joursCongesAnnuels: string; traitementHeuresSup: string;
  } | null>(null);
  /** Le contrat signé choisi dans le formulaire — envoyé APRÈS l'écriture du
   *  contrat (il faut son identifiant). */
  const [contratFichier, setContratFichier] = useState<File | null>(null);
  const [form, setForm] = useState<Brouillon>(BROUILLON_VIDE);
  /** CE QUE L'ONGLET AVAIT LU À SON OUVERTURE (14/10/2026), figé : envoyé
   *  avec l'enregistrement (`base`), il permet au serveur de n'écrire que ce
   *  que l'utilisateur a changé, et de refuser si un autre a changé le même
   *  champ pendant la saisie (fusion à trois voies, `fusion-formulaire.ts`). */
  const [baseLue, setBaseLue] = useState<Brouillon>(BROUILLON_VIDE);
  /** Changement de statut en attente de motif (remplace `window.prompt()`, que
   *  le navigateur intégré refuse : les boutons ne faisaient alors RIEN). */
  const [demande, setDemande] = useState<
    { statut: 'actif' | 'suspendu' | 'sorti'; titre: string; question: string; action: string } | null
  >(null);
  const [motif, setMotif] = useState('');

  const isRQ = session.roles.includes('rq');
  const aRh = !!rhAcces?.acces;

  const users: GedUser[] = settings?.gedUsers || [];
  const rh = rhPersonnel.find((x) => x.orgId === orgId);

  /* ── DROITS (RQ, 02/10/2026) : toute modification de la fiche et de ses
   * dépendances — contrats, statut, identifiant — relève du RQ OU du RH.
   * ⚠️ Il faut aussi que la fiche SOIT servie (`rh`) : le brouillon part d'elle,
   * et un brouillon parti de la vue organigramme (sans matricule, badge,
   * téléphone) effacerait ces champs au premier enregistrement. Le serveur
   * revérifie chaque route : ceci décide de ce qu'on PROPOSE. */
  const peutEcrire = (isRQ || aRh) && !!rh;
  /** L'identifiant de connexion : RQ, RH — et le super admin, qui tient
   *  l'annuaire des comptes (03/10/2026, même règle que le moteur). */
  const peutChangerIdentifiant = peutEcrire || (session.profile === 'superadmin' && !!rh);
  const voitAdmin = (isRQ || aRh) && !!rh;
  const voitJournal = !!session.sections?.rhjournal?.voir;

  /**
   * LA FICHE S'OUVRE AUSSI HORS ORGANIGRAMME (01/09/2026) : `gedUsers` est la
   * VUE de l'organigramme, `rh-personnel` la SOURCE. Le repli reconstruit la fiche
   * depuis la source — un `null` ici ouvrait « rien », sans un mot.
   */
  const uOrg = users.find((x) => x.id === orgId);
  const horsOrganigramme = !uOrg && !!rh;
  const u: GedUser | undefined = uOrg || (rh ? {
    id: rh.orgId, nom: nomDe(rh), initiales: rh.initiales || '', fonction: rh.fonction || '',
    site: rh.site || '', roles: rh.roles || [],
    orgTier: rh.orgTier || undefined,
    responsable: rh.responsableOrgId || null,
    lien: rh.lienResponsable === 'fonctionnel' ? 'fonctionnel' : 'hierarchique',
    fonctionIds: rh.fonctionIds || [],
    statut: rh.statut,
  } : undefined);
  /** « Prénom Nom », composé — jamais `rh.nom`, qui est le patronyme seul. */
  const nomAff = rh ? nomDe(rh) : (u?.nom || '');

  // Échap ferme la modale de motif si elle est ouverte, PAS le dossier derrière.
  const boiteDossier = React.useRef<HTMLElement | null>(null);
  /* ⚠️ Une seule porte de sortie : la croix (Échap passe par la même, alerte
     comprise). `useEscape(…, onClose)` contournait toute garde. */
  const refusDossier = useSortieFenetre({ boite: boiteDossier, onClose, actif: !demande });
  /* La page de dessous ne défile plus (comme `Modale`). */
  useVerrouScroll(true);

  useEffect(() => {
    if (!voitAdmin) return;
    let vivant = true;
    void chargerContrats(orgId).then((c) => { if (vivant) setContrats(c); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, voitAdmin]);

  /* ── LES DEUX ONGLETS QUI LISENT AILLEURS SE CHARGENT À L'OUVERTURE ───────
   *
   * Soldes, absences et journal RH ne sont pas dans la fiche : ils vivent dans
   * leurs propres registres, que seuls leurs écrans chargeaient. On les demande
   * donc quand l'onglet s'ouvre, et UNE fois — ouvrir un dossier ne doit pas
   * coûter quatre requêtes dont trois pour des onglets qu'on ne regardera pas.
   *
   * ⚠️ « UNE fois » SE GARDE, il ne se souhaite pas. Sans témoin, l'effet
   * repart à CHAQUE retour sur l'onglet : aller voir les compétences puis
   * revenir aux congés relançait cinq requêtes (`loadRhGestion` en fait cinq).
   * Le témoin est une référence et non un état : le poser ne doit pas
   * provoquer de rendu, et `react-hooks/set-state-in-effect` est une erreur
   * dans ce projet.
   *
   * ⚠️ Le test du journal porte sur `rhJournal !== null` et non sur sa
   * longueur : un journal vide et un journal refusé ne se distinguent que comme
   * ça, et relire en boucle une lecture refusée est une requête par rendu.
   */
  /* ── LES PIÈCES SE LISENT PAR PERSONNE (08/10/2026) ──────────────────────
   * Elles étaient portées par la liste des parcours (`rhIntegrations`), qui ne
   * couvre que ceux qui en ont un — le trou qu'on referme. `null` = pas encore
   * lu OU lecture refusée ; `[]` = lu, aucune pièce. Les confondre ferait
   * afficher « aucune pièce » à qui n'a pas eu le droit de demander. */
  const [pieces, setPieces] = useState<RhIntegrationPiece[] | null>(null);
  const relirePieces = React.useCallback(async () => {
    setPieces(await chargerPiecesPersonne(orgId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  const piecesDemandees = React.useRef(false);
  useEffect(() => {
    if (onglet !== 'pieces' || !aRh || piecesDemandees.current) return;
    piecesDemandees.current = true;
    void relirePieces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet, aRh]);

  const gestionDemandee = React.useRef(false);
  useEffect(() => {
    if (onglet !== 'temps' || !voitAdmin || gestionDemandee.current) return;
    gestionDemandee.current = true;
    void loadRhGestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet, voitAdmin]);
  useEffect(() => {
    if (onglet !== 'journal' || !voitJournal || rhJournal !== null) return;
    void loadJournalRh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet, voitJournal, rhJournal]);

  /* ── INITIALES : l'unicité se dit À LA SAISIE ─────────────────────────────
   * ⚠️ SUR L'HISTORIQUE COMPLET, sortis compris — arbitrage RQ du 02/09/2026 :
   * les plannings archivés sont des enregistrements conservés, libérer les
   * initiales d'un partant les rendrait ambiguës rétroactivement. Le serveur
   * applique la même règle ; ceci ne fait que la dire plus tôt. */
  const initialesSaisies = form.initiales.trim().toUpperCase();
  const conflitInitiales = enSaisie === 'identite' && initialesSaisies
    ? rhPersonnel.find((x) => x.orgId !== orgId
      && String(x.initiales || '').trim().toUpperCase() === initialesSaisies) || null
    : null;
  /** Deux ou trois formes libres, proposées seulement en cas de conflit. Elles
   *  se composent sur « prénom + nom » : `nom` est le patronyme seul. */
  const suggestionsInitiales = React.useMemo(() => {
    if (!conflitInitiales) return [];
    const prises = new Set(rhPersonnel.map((x) => String(x.initiales || '').trim().toUpperCase()).filter(Boolean));
    const mots = `${form.prenom} ${form.nom}`.trim().split(/\s+/).filter(Boolean);
    const p0 = (mots[0] || '').toUpperCase(); const n0 = (mots[1] || '').toUpperCase();
    const cand = [
      p0[0] && n0[0] && n0[1] ? p0[0] + n0[0] + n0[1] : '',
      p0[0] && p0[1] && n0[0] ? p0[0] + p0[1] + n0[0] : '',
      p0[0] && n0[0] && mots[2] ? p0[0] + n0[0] + mots[2][0].toUpperCase() : '',
    ].filter((x) => x.length >= 2 && !prises.has(x));
    return [...new Set(cand)].slice(0, 3);
  }, [conflitInitiales, rhPersonnel, form.prenom, form.nom]);

  /* ── COULEUR : la teinte EFFECTIVE, celle du planning ─────────────────────
   * `pastilleDe(orgId, couleur)` — le hachage de l'identifiant quand rien n'est
   * choisi. La fiche, l'organigramme et le planning lisent donc la même chose.
   * Les teintes prises le sont par les collègues de MÊME FONCTION (cases de
   * l'organigramme qui se recoupent — la règle du serveur), jamais par tout le
   * monde : deux fonctions différentes peuvent partager une couleur sans gêne. */
  const couleurActuelle = enSaisie === 'identite' ? form.couleur : (rh?.couleur || '');
  const teinteAff = teinteEffective(orgId, couleurActuelle);
  const fonctionsAff = enSaisie === 'organisation' ? form.fonctionIds : (rh?.fonctionIds || []);
  const aujourdhui = aujourdhuiIso();
  /* Pas de `useMemo` manuel : le compilateur React refuse de préserver celui-ci
     (dépendance « modifiable plus tard ») et abandonnerait l'optimisation de
     tout le composant. Trente fiches, le calcul est négligeable. */
  const prisesCouleur = couleursPrises(
    rhPersonnel.map((x) => ({
      orgId: x.orgId, nom: nomDe(x), fonctionIds: x.fonctionIds, statut: x.statut,
      dateSortie: x.dateSortie, couleur: x.couleur, externe: x.type === 'externe',
    })), orgId, fonctionsAff, aujourdhui,
  );
  if (!u) return null;

  /* ── CONTRAT (arbitrage RQ, 02/09/2026) ──────────────────────────────────
   * ⚠️ UN CONTRAT NE SE RÉÉCRIT PAS QUAND IL EST CLOS : on en ouvre un nouveau,
   * l'ancien reste lisible (quel régime s'appliquait en mars ?). Valeurs par
   * défaut = celles du laboratoire (40 h, 100 %) ; le droit à congé part VIDE —
   * le deviner poserait un solde faux. */
  const ouvrirContrat = (c: RhContrat | null) => {
    setContratFichier(null);
    setContratEdite({
      documentId: c?.documentId || '',
      contratType: c?.contratType || 'cdi',
      dateDebut: c?.dateDebut || rh?.dateEntree || '',
      dateFin: c?.dateFin || '',
      regimeHebdo: c?.regimeHebdo != null ? String(c.regimeHebdo) : '40',
      quotite: c?.quotite != null ? String(c.quotite) : '100',
      joursCongesAnnuels: c?.joursCongesAnnuels != null ? String(c.joursCongesAnnuels) : '',
      traitementHeuresSup: c?.traitementHeuresSup || 'repos',
    });
  };
  const majContrat = (k: string, v: string) => setContratEdite((x) => (x ? { ...x, [k]: v } : x));
  const enregistrerLeContrat = async () => {
    if (!contratEdite) return;
    setBusy(true);
    const id = await enregistrerContratId({
      ...(contratEdite.documentId ? { documentId: contratEdite.documentId } : {}),
      personnelOrgId: orgId,
      contratType: contratEdite.contratType,
      dateDebut: contratEdite.dateDebut,
      dateFin: contratEdite.dateFin || '',
      regimeHebdo: Number(contratEdite.regimeHebdo) || undefined,
      quotite: Number(contratEdite.quotite) || 100,
      joursCongesAnnuels: contratEdite.joursCongesAnnuels === '' ? null : Number(contratEdite.joursCongesAnnuels),
      traitementHeuresSup: contratEdite.traitementHeuresSup,
    });
    /* ⚠️ La pièce part APRÈS le contrat, et un échec de la pièce ne défait pas
       le contrat : il est écrit, l'écran le dit (toast) et la pièce se rejoint
       depuis la ligne du contrat. Sans cela on ne saurait pas lequel des deux a
       échoué. */
    if (id && contratFichier) await joindrePieceContrat(id, contratFichier);
    setBusy(false);
    if (id) {
      setContratEdite(null); setContratFichier(null);
      /* La liste est locale à cette fiche : `loadRh` ne la rafraîchit pas. */
      setContrats(await chargerContrats(orgId));
      /* La saisie du contrat vit DANS l'onglet : elle a compté comme une
         frappe, et elle est enregistrée. Sans cela la croix demanderait
         ensuite de confirmer l'abandon d'un contrat déjà en base. */
      refusDossier.saisieEnregistree();
    }
  };
  /** Dépose ou remplace le contrat signé d'un contrat DÉJÀ enregistré. */
  const joindreAuContrat = async (contratId: string, f: File) => {
    setBusy(true);
    const ok = await joindrePieceContrat(contratId, f);
    setBusy(false);
    if (ok) setContrats(await chargerContrats(orgId));
  };
  const retirerDuContrat = async (contratId: string) => {
    setBusy(true);
    const ok = await retirerPieceContrat(contratId);
    setBusy(false);
    if (ok) setContrats(await chargerContrats(orgId));
  };

  const compte = rh?.compteEtat || null;
  const etatCompte = compte ? ETAT_COMPTE[compte.etat] : null;
  const adresseDivergente = !!compte?.email && !!rh?.email
    && compte.email.trim().toLowerCase() !== rh.email.trim().toLowerCase();

  const statut = String(u.statut || rh?.statut || 'actif');
  const badge = STATUT_DOSSIER[statut] || STATUT_DOSSIER.actif;

  const responsable = users.find((x) => x.id === u.responsable);
  const encadres = users.filter((x) => x.responsable === u.id);
  /* Qui peut porter le trait : l'effectif en service, SOI-MÊME exclu (le serveur
     refuse l'auto-rattachement) et les externes écartés. */
  const encadrants = rhPersonnel
    .filter((p) => p.orgId !== orgId && p.statut !== 'sorti' && p.type !== 'externe')
    .slice()
    .sort((a, b) => nomDe(a).localeCompare(nomDe(b), 'fr'));
  const fonctionsTenues = (settings?.orgFonctions || [])
    .filter((f) => (u.fonctionIds || []).includes(f.id));

  const compétences = Object.entries(settings?.habilitations?.[orgId] || {});
  const habilite = compétences.filter(([, v]) => v === 'habilite').map(([d]) => d);
  const enFormation = compétences.filter(([, v]) => v === 'formation').map(([d]) => d);

  const processus = settings?.processus || [];
  const pilote = processus.filter((p) => p.pilote === orgId);
  const supplee = processus.filter((p) => p.suppleant === orgId);

  // Parcours documentaire : TOUS les documents, y compris obsolètes et retirés.
  const aRediges = docs.filter((d) => [...(d.redacteurs || []), d.redacteur].includes(orgId));
  const aVerifies = docs.filter((d) => d.verificateur === orgId);
  const aApprouves = docs.filter((d) => d.approbateur === orgId);

  /* ── CE QUE LES ONGLETS « TEMPS » ET « PIÈCES » RASSEMBLENT ───────────────
   * Rien n'est recalculé ici : le solde vient du moteur (il dépend d'un mode
   * paramétrable), les absences du registre, le parcours de l'intégration. */
  const solde = rhSoldes.find((s) => s.orgId === orgId) || null;
  const absences = rhAbsences
    .filter((a) => a.personnel?.orgId === orgId)
    .slice()
    .sort((a, b) => String(b.dateDebut).localeCompare(String(a.dateDebut)));
  const integration = rhIntegrations.find((i) => i.orgId === orgId) || null;
  const contratActif = (contrats || []).find((c) => c.actif) || null;

  /**
   * LE JOURNAL DE LA PERSONNE — par RAPPROCHEMENT DU LIBELLÉ, et c'est dit.
   *
   * ⚠️ Les événements du journal RH ne portent AUCUN champ « personne » : la
   * trace est chaînée et signée, pas relationnelle, et son libellé est la
   * phrase scellée dans l'empreinte. Le filtre cherche donc le NUMÉRO de
   * personne (« P-0007 », présent dans les libellés depuis le 13/09/2026) et, à
   * défaut, le nom composé.
   *
   * ⚠️ C'est un rapprochement, pas une jointure : un acte peut manquer à cette
   * liste. L'écran le DIT et renvoie au journal complet — laisser croire à
   * l'exhaustivité serait pire que la liste incomplète, parce qu'on en
   * conclurait « il ne s'est rien passé ».
   *
   * ⚠️ PAS DE `useMemo` : même raison que `prisesCouleur` plus haut — le
   * compilateur React refuse de préserver une mémoïsation dont les dépendances
   * sont « modifiables plus tard », et il abandonne alors l'optimisation de TOUT
   * le composant (la règle `react-hooks/preserve-manual-memoization` est en
   * erreur). Le journal lu fait quelques centaines de lignes : un filtre par
   * rendu est négligeable, l'optimisation perdue ne l'est pas.
   */
  const journalPersonne = (() => {
    if (!rhJournal) return null;
    const cles = [rh?.numero, nomAff].map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);
    if (!cles.length) return [];
    return rhJournal.filter((e) => {
      const p = String(e.payload || '').toLowerCase();
      return cles.some((c) => p.includes(c));
    }).slice(0, 40);
  })();

  /* Le motif reste FACULTATIF (arbitrage RQ) : une réactivation de retour de
     congé n'a rien à expliquer. */
  const majStatut = async () => {
    if (!demande) return;
    setBusy(true);
    const ok = await changerStatutPersonne(orgId, demande.statut, motif);
    setBusy(false);
    if (ok) { setDemande(null); setMotif(''); }
  };

  /* Le brouillon part de `rh` — la SOURCE — et non de `u`, la vue recomposée :
     elle ne porte ni prénom, ni matricule, ni téléphone. */
  /* ── LE ROND EST LE SÉLECTEUR DE COULEUR (RQ, 05/10/2026) ────────────────
   *
   * « Au lieu d'écrire choisie devant la pastille colorée, juste permettre de
   * changer la couleur en appuyant sur le rond avec les initiales. »
   *
   * En lecture, le rond fait donc entrer en modification AVEC la palette
   * ouverte : sans cela il faudrait appuyer deux fois, et le second clic ne se
   * devine pas. ⚠️ Rien n'est écrit pour autant — la couleur part avec
   * « Enregistrer », comme les autres champs de l'onglet. Un rond qui écrirait
   * en base au clic serait une seconde porte d'écriture sur un onglet qui en a
   * déjà une.
   *
   * ⚠️ IL RAMÈNE À L'ONGLET « IDENTITÉ » (06/10/2026) : la couleur appartient à
   * ce brouillon-là, et depuis que l'édition est par onglet, l'ouvrir depuis
   * « Contrat » aurait mis en saisie un onglet qui n'est pas celui affiché.
   */
  const ouvrirEdition = (cible: OngletEditable, parLaCouleur = false) => {
    setPaletteALOuverture(parLaCouleur);
    const lu: Brouillon = {
      nom: rh?.nom || '', prenom: rh?.prenom || '', initiales: rh?.initiales || '',
      couleur: rh?.couleur || '',
      dateNaissance: rh?.dateNaissance || '', adresse: rh?.adresse || '',
      site: rh?.site || '', orgTier: rh?.orgTier || '',
      responsableOrgId: rh?.responsableOrgId || '',
      lienResponsable: rh?.lienResponsable === 'fonctionnel' ? 'fonctionnel' : 'hierarchique',
      matriculePaie: rh?.matriculePaie || '',
      coefficientGarde: rh?.coefficientGarde != null ? String(rh.coefficientGarde) : '',
      rib: rh?.rib || '', banque: rh?.banque || '',
      badgeId: rh?.badgeId || '',
      email: rh?.email || '', telephone: rh?.telephone || '',
      dateEntree: rh?.dateEntree || '', dateSortie: rh?.dateSortie || '',
      /* Absent en base = SOUMIS (le défaut du champ est `true`). */
      soumisAuPointage: rh?.soumisAuPointage !== false,
      soumisAuxConges: rh?.soumisAuxConges !== false,
      pointageDepuis: rh?.pointageDepuis || '',
      fonctionIds: (rh?.fonctionIds || []).slice(),
      rolesOrg: (rh?.rolesOrg || []).slice(),
      pilotages: processus.filter((x) => x.pilote === orgId).map((x) => x.id),
      suppleances: processus.filter((x) => x.suppleant === orgId).map((x) => x.id),
    };
    setForm(lu);
    // Copie séparée (tableaux compris) : la saisie ne doit jamais modifier la base.
    setBaseLue({ ...lu, fonctionIds: lu.fonctionIds.slice(), rolesOrg: lu.rolesOrg.slice(), pilotages: lu.pilotages.slice(), suppleances: lu.suppleances.slice() });
    setOnglet(cible);
    setEnSaisie(cible);
  };
  const majF = <K extends keyof Brouillon>(k: K, v: Brouillon[K]) =>
    setForm((x) => ({ ...x, [k]: v }));

  const emailInvalide = !!form.email.trim() && !MAIL_OK(form.email);
  /** Ce qui empêche d'enregistrer L'ONGLET OUVERT — les autres ne sont pas
   *  en saisie, leurs champs n'ont donc rien à bloquer. */
  const raisonBloquante = enSaisie !== 'identite' ? ''
    : !form.nom.trim() ? 'Le nom ne peut pas être vide.'
      : conflitInitiales ? `« ${initialesSaisies} » est déjà attribué à ${nomDe(conflitInitiales)}.`
        : emailInvalide ? 'Le courriel n’est pas une adresse valide.' : '';

  /* ⚠️ CETTE FENÊTRE NE SE REFERME PAS EN ENREGISTRANT — elle repasse en lecture
     et reste ouverte (on vérifie ce qu'on vient d'écrire). Il faut dire au
     garde-fou de sortie que la saisie est sauvée, sinon la croix demande ensuite
     de confirmer l'abandon de modifications déjà en base. `saisieEnregistree`
     n'est appelé que sur un `ok`. */
  const enregistrerOnglet = async () => {
    if (!enSaisie) return;
    /* ⚠️ SEULS LES CHAMPS DE L'ONGLET PARTENT. La route n'écrit que ce qu'elle
       reçoit : ce découpage est donc ce qui garantit qu'enregistrer « Contrat »
       ne peut pas réécrire le nom avec une valeur lue il y a dix minutes. */
    const envoi: Partial<Brouillon> = {};
    for (const k of CHAMPS_ONGLET[enSaisie]) envoi[k] = form[k] as never;
    /* `base` (14/10/2026) : les mêmes champs, tels que LUS à l'ouverture. */
    const base: Partial<Brouillon> = {};
    for (const k of CHAMPS_ONGLET[enSaisie]) base[k] = baseLue[k] as never;
    setBusy(true);
    const ok = await majPersonnelRh(orgId, { ...envoi, base });
    setBusy(false);
    if (ok) { refusDossier.saisieEnregistree(); setEnSaisie(null); onEnregistre?.(); }
  };
  /** ⚠️ ANNULER EFFACE AUSSI LA MARQUE DE SAISIE, et c'est volontaire : après
   *  un abandon il n'y a plus RIEN à perdre, et une alerte de sortie qui porte
   *  sur un brouillon jeté est exactement celle qu'on apprend à ne plus lire
   *  (cf. l'en-tête de `fenetreModale`). Le besoin est devenu quotidien avec
   *  les onglets : on annule ici, on continue de consulter trois onglets, puis
   *  on ferme — la question n'a plus d'objet. */
  const annulerOnglet = () => { setEnSaisie(null); refusDossier.saisieEnregistree(); };

  const TITRE_COL: React.CSSProperties = { display: 'grid', gap: 14, alignContent: 'start', minWidth: 0 };
  const pastilleEntete = pastilleDe(orgId, couleurActuelle);
  const initialesAff = (enSaisie === 'identite' ? form.initiales : (rh?.initiales ?? u.initiales)) || initials(nomAff);
  /* Le rond, une fois pour les trois cas (sélecteur, bouton, simple pastille) :
     dupliqué, il aurait fini par changer de taille selon le droit d'écriture. */
  const rondEntete = (
    <span aria-hidden style={{
      width: 52, height: 52, flex: 'none', borderRadius: '50%',
      background: pastilleEntete.background, color: pastilleEntete.color,
      border: `2px solid ${pastilleEntete.borderColor}`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700,
    }}>{initialesAff}</span>
  );

  /** Un onglet réservé n'est pas CACHÉ, il est désactivé et dit pourquoi : le
   *  masquer ferait croire que le dossier n'a que trois rubriques. */
  const refusOnglet = (o: { cle: Onglet; reserve?: boolean }): string => {
    if (enSaisie && enSaisie !== o.cle) {
      return 'Enregistrez ou annulez la saisie en cours : un brouillon ne suit pas d’un onglet à l’autre.';
    }
    if (o.reserve && !voitAdmin) {
      return 'Rubrique réservée au gestionnaire RH et au responsable qualité.';
    }
    return '';
  };

  const ongletStyle = (actif: boolean, bloque: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '9px 12px', whiteSpace: 'nowrap',
    background: 'none', border: 'none', cursor: bloque ? 'not-allowed' : 'pointer',
    opacity: bloque ? 0.45 : 1,
    color: actif ? 'var(--color-primary)' : 'var(--text-muted)',
    borderBottom: `2px solid ${actif ? 'var(--color-primary)' : 'transparent'}`,
  });

  /** Quitter la fenêtre pour un autre écran : jamais en saisie — on
   *  abandonnerait un brouillon sans passer par la garde de sortie. */
  const allerVers = (vue: Parameters<typeof naviguerVers>[0]) => { onClose(); naviguerVers(vue); };

  return (
    <>
      {/* ── LA FICHE EST UNE FENÊTRE MODALE CLASSIQUE (RQ, 02/10/2026), large,
          centrée — plus un panneau latéral. Même structure que `Modale` : voile
          qui ne ferme rien (la sortie est la croix), boîte à défilement interne.
          ⚠️ z-index = `COUCHES.MODALE` (400) : la bannière de l'application est
          collante à 80, un panneau posé plus bas passait dessous. Les fenêtres
          filles (motif, identifiant) sont posées APRÈS dans le DOM, donc
          au-dessus à égalité de couche. */}
      <div {...refusDossier.proprietesVoile}
        style={{
          position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
        <section ref={boiteDossier} role="dialog" aria-modal="true" aria-label={`Dossier de ${nomAff}`}
          {...refusDossier.proprietesBoite}
          style={{
            ...card, width: 'min(1160px, 100%)', maxHeight: '94vh', display: 'flex', flexDirection: 'column',
            overflow: 'hidden', position: 'relative',
            ...refusDossier.proprietesBoite.style,
          }}>
          {refusDossier.alerte && <AlerteSortie onRester={refusDossier.rester} onQuitter={refusDossier.quitterSansEnregistrer} />}

          {/* ---- En-tête (fixe) : nom, numéro, états, actions de dossier ----
              ⚠️ L'EN-TÊTE NE SE SAISIT PLUS (06/10/2026). Nom, prénom et
              initiales y étaient des champs : ils sont descendus dans l'onglet
              « Identité », avec le reste de ce qu'ils composent. L'en-tête
              IDENTIFIE la personne — il est le même sur les six onglets, et un
              bandeau qui change de forme selon l'onglet se lit comme un autre
              écran. Seul le ROND reste cliquable : il ouvre la couleur. */}
          <div style={{ flex: 'none', padding: '18px 22px 0', borderBottom: '1px solid var(--border-default)' }}>
            {/* `ged-entete-dossier` : sur téléphone, l'identité passe sous la
                rangée pastille + boutons (globals.css, 19/10/2026) — à côté
                d'eux, il ne lui restait que 80 px, un mot par ligne. */}
            <div className="ged-entete-dossier" style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              {/* La pastille suit la saisie AVANT enregistrement (spec Lot 2 :
                  « la couleur doit être visible immédiatement »). */}
              {enSaisie === 'identite' && rh ? (
                <SelecteurCouleur teinte={teinteAff} initiales={initialesAff}
                  prises={prisesCouleur} onChoisir={(v) => majF('couleur', v)}
                  ouvertAuMontage={paletteALOuverture} declencheur={rondEntete} />
              ) : peutEcrire && !enSaisie ? (
                <button type="button" className="ged-btn" onClick={() => ouvrirEdition('identite', true)}
                  aria-label={`Changer la couleur de ${nomAff}`}
                  title="Changer la couleur — celle du planning et de l’organigramme"
                  style={{ background: 'none', border: 'none', padding: 0, borderRadius: '50%', cursor: 'pointer', display: 'inline-flex', flex: 'none' }}>
                  {rondEntete}
                </button>
              ) : rondEntete}
              <div className="ged-entete-dossier-identite" style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--text-strong)', display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                  {nomAff}
                  {initialesAff && (
                    <span title="Initiales" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>
                      {initialesAff}
                    </span>
                  )}
                </h2>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{rh?.intitule || u.fonction}</div>
                {/* ── LE NUMÉRO DE PERSONNE (13/09/2026) — l'identité qui PROUVE.
                    Affiché ICI, en tête : ce n'est pas une donnée de paie, et il
                    désigne la fiche entière. En chasse fixe et sélectionnable : il
                    se recopie dans une recherche au journal.
                    ⚠️ Il n'est servi qu'AVEC la fiche du personnel (`rh`) : sans
                    elle, il n'y a rien à afficher et on ne l'invente pas. */}
                {!!rh?.numero && (
                  <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em',
                      color: 'var(--text-strong)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-sm)', padding: '3px 9px', userSelect: 'all' }}>{rh.numero}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      Numéro de personne — c’est lui qui signe ses actes au journal. Il ne change jamais
                      et ne sert pas à se connecter (voir « Identifiant de connexion », onglet Identité).
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: badge.bg, color: badge.fg }}>
                    {badge.label}
                  </span>
                  {/* L'ÉTAT DU COMPTE, servi par `/ged/rh/personnel`. ⚠️ Rien n'est
                      affirmé quand `rh` est absent : son absence ne veut pas dire
                      « pas de compte » (BLOQ-03). */}
                  {rh && (etatCompte
                    ? (
                      <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: etatCompte.bg, color: etatCompte.fg }}>
                        {etatCompte.label}
                      </span>
                    )
                    : (
                      <span style={{ fontSize: 10.5, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: 'var(--gray-50)', color: 'var(--text-muted)' }}>
                        Sans compte de connexion
                      </span>
                    ))}
                  {horsOrganigramme && (
                    <span title="Elle n’apparaît pas sur le document d’organisation et ne peut pas être déplacée dans l’arbre."
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>
                      Hors organigramme
                    </span>
                  )}
                </div>
              </div>
              {/* Le parcours d'habilitation de la personne, sur une frise (17/10/2026). */}
              <Bouton taille="sm" onClick={() => ouvrirDepuis('dossier-personnel', { view: 'parcours', cle: 'selectedPersonne', valeur: orgId })}
                title="De l’accueil au maintien des compétences : où elle en est, et qui doit agir">
                <I n="clipboardCheck" s={14} /> Parcours
              </Bouton>
              <Bouton taille="sm" onClick={refusDossier.demanderSortie} aria-label="Fermer le dossier"
                style={refusDossier.refuse ? { background: 'var(--color-primary-soft)', color: 'var(--color-primary-active)' } : undefined}>
                <I n="x" s={14} />
              </Bouton>
            </div>

            {/* ---- Actions de DOSSIER (jamais pendant une saisie) ----
                Suspendre ou transférer pendant qu'un onglet est en saisie ferait
                partir deux écritures sur la même personne, dont une depuis un
                brouillon. Ces trois-là portent sur le dossier ENTIER : elles ne
                sont donc dans aucun onglet. */}
            {/* ⚠️ « TRANSFÉRER SES RESPONSABILITÉS » A QUITTÉ CETTE BARRE le
                06/10/2026 (RQ). Le bouton était ici, mais le panneau de transfert
                vit dans l'organigramme — il est long et se remplit au fil des
                choix. Il fermait donc le dossier, affichait « le transfert se
                fait depuis l'organigramme », vous y envoyait… et il fallait
                RECLIQUER la personne dans l'arbre, puis recliquer le bouton. Un
                bouton qui fait un détour est pire qu'un renvoi écrit.
                Le geste vit maintenant sur le résumé de l'organigramme, à un
                clic de l'endroit où l'on se pose la question — « qui reprend ? »
                se regarde sur l'arbre. */}
            {(isRQ || aRh) && !enSaisie && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {peutEcrire && statut === 'actif' && (
                  <>
                    <Bouton taille="sm" onClick={() => { setMotif(''); setDemande({ statut: 'suspendu', titre: `Suspendre le dossier de ${nomAff}`, question: 'Suspension (absence longue) — motif :', action: 'Suspendre' }); }} disabled={busy}>
                      <I n="pause" s={12} />Suspendre
                    </Bouton>
                    <Bouton taille="sm" onClick={() => { setMotif(''); setDemande({ statut: 'sorti', titre: `Archiver le dossier de ${nomAff}`, question: 'Archivage (départ de l’établissement) — motif :', action: 'Archiver' }); }} disabled={busy}>
                      <I n="archive" s={12} />Archiver
                    </Bouton>
                  </>
                )}
                {peutEcrire && statut !== 'actif' && (
                  <Bouton variante="primaire" taille="sm" onClick={() => { setMotif(''); setDemande({ statut: 'actif', titre: `Réactiver le dossier de ${nomAff}`, question: 'Réactivation — motif :', action: 'Réactiver le dossier' }); }} disabled={busy}>
                    <I n="check" s={12} />Réactiver le dossier
                  </Bouton>
                )}
              </div>
            )}
            {(isRQ || aRh) && !rh && (
              <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '10px 0 0', lineHeight: 1.5 }}>
                La liste du personnel n’est pas servie à ce compte : le dossier s’affiche en lecture
                seule, sans coordonnées, sans numéro de personne et sans bouton de modification.
              </p>
            )}

            {/* ---- LES ONGLETS, et le bouton « Modifier » de l'onglet ouvert ----
                ⚠️ `Modifier` est À L'EXTRÉMITÉ DE LA BARRE D'ONGLETS et non dans
                le corps : il porte sur l'onglet affiché, et c'est le seul endroit
                où les deux sont côte à côte. Il disparaît en saisie — le pied
                collé en bas prend alors le relais. */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginTop: 14, overflowX: 'auto' }}>
              <div role="tablist" aria-label="Rubriques du dossier" style={{ display: 'flex', gap: 2, flex: 1, minWidth: 0 }}>
                {ONGLETS_DOSSIER.map((o) => {
                  const bloque = !!refusOnglet(o);
                  return (
                    <button key={o.cle} type="button" role="tab" aria-selected={onglet === o.cle}
                      disabled={bloque} title={refusOnglet(o) || undefined}
                      onClick={() => setOnglet(o.cle)} style={ongletStyle(onglet === o.cle, bloque)}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {peutEcrire && !enSaisie && estEditable(onglet) && (
                <Bouton taille="sm" style={{ flex: 'none', marginBottom: 6 }} onClick={() => ouvrirEdition(onglet)}>
                  <I n="pen" s={12} />Modifier
                </Bouton>
              )}
            </div>
          </div>

          {/* ---- Corps (défile) : l'onglet ouvert, sur deux colonnes quand il
                  a de quoi les remplir ---- */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 22px 28px' }}>

            {/* ════════ ONGLET 1 — IDENTITÉ ════════ */}
            {onglet === 'identite' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: 14, alignItems: 'start' }}>
                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Identité &amp; coordonnées</h3>
                    {!rh ? (
                      <Ligne label="Intitulé">{u.fonction || vide}</Ligne>
                    ) : enSaisie === 'identite' ? (
                      <>
                        {/* ⚠️ NOM, PRÉNOM ET INITIALES SONT ICI DEPUIS LE 06/10/2026,
                            et plus dans l'en-tête : ils composent l'identité, c'est
                            donc l'onglet de l'identité qui les porte. Leur contrôle
                            d'unicité et les suggestions vivent avec eux, et nulle
                            part ailleurs. */}
                        <LigneSaisie label="Prénom">
                          <input value={form.prenom} placeholder="Prénom" aria-label="Prénom" style={inputStyle}
                            onChange={(e) => majF('prenom', e.target.value)} />
                        </LigneSaisie>
                        <LigneSaisie label="Nom"
                          aide="Le nom de famille seul. Il s’affiche toujours après le prénom.">
                          <input value={form.nom} placeholder="Nom" aria-label="Nom" style={inputStyle}
                            onChange={(e) => majF('nom', e.target.value)} />
                        </LigneSaisie>
                        <LigneSaisie label="Initiales"
                          aide={conflitInitiales
                            ? (
                              <span style={{ color: 'var(--red-600)' }}>
                                « {form.initiales} » est déjà attribué à <strong>{nomDe(conflitInitiales)}</strong>
                                {conflitInitiales.statut === 'sorti'
                                  ? ' — sortie des effectifs, mais ses initiales restent réservées : les plannings archivés la désignent.'
                                  : '.'}
                                {!!suggestionsInitiales.length && <> Disponibles : {suggestionsInitiales.join(' · ')}.</>}
                              </span>
                            )
                            : <>Affiché partout : <strong style={{ color: 'var(--text-strong)' }}>{nomDe({ titre: rh?.titre, prenom: form.prenom, nom: form.nom }) || '—'}</strong>
                              {initialesSaisies && <> ({initialesSaisies})</>}. Deux ou trois majuscules — c’est tout ce
                              qu’on lit d’elle dans le planning mensuel, et elles ne se partagent pas.</>}>
                          <input value={form.initiales} placeholder={initials(nomAff)} maxLength={3} aria-label="Initiales"
                            style={{ ...inputStyle, maxWidth: 110, fontFamily: 'var(--font-mono)', textAlign: 'center' }}
                            onChange={(e) => majF('initiales', e.target.value.toUpperCase())} />
                        </LigneSaisie>
                        {/* ⚠️ PAS DE LIGNE « COULEUR » ICI (RQ, 05/10/2026) : elle
                            se change sur le ROND de l'en-tête, deux fois plus
                            gros et toujours à l'écran. Un champ de plus aurait
                            fait deux endroits pour un seul réglage — et c'est
                            toujours le second qu'on oublie de corriger. */}
                        <LigneSaisie label="Date de naissance">
                          <input type="date" value={form.dateNaissance} max={aujourdhuiIso()}
                            style={{ ...inputStyle, maxWidth: 220 }}
                            onChange={(e) => majF('dateNaissance', e.target.value)} />
                        </LigneSaisie>
                        <LigneSaisie label="Adresse">
                          <input type="text" value={form.adresse} autoComplete="street-address" style={inputStyle}
                            placeholder="Rue, ville, code postal"
                            onChange={(e) => majF('adresse', e.target.value)} />
                        </LigneSaisie>
                        {/* UN SEUL CHAMP DE COURRIEL (RQ, 02/10/2026). Le serveur
                            écrit la fiche ET le compte de connexion : l'ancien bouton
                            « Modifier l'adresse », séparé, ne servait plus qu'à
                            dupliquer la saisie. */}
                        <LigneSaisie label="Courriel"
                          aide={emailInvalide
                            ? <span style={{ color: 'var(--red-600)' }}>Cette adresse n’est pas valide.</span>
                            : compte
                              ? <>Une seule adresse pour la fiche <strong>et</strong> le compte de connexion : enregistrer met les deux à jour.</>
                              : undefined}>
                          <input type="email" value={form.email} style={inputStyle} autoComplete="off"
                            onChange={(e) => majF('email', e.target.value)} />
                        </LigneSaisie>
                        {/* Indicatif séparé, +216 (Tunisie) par défaut ; liste déroulante pour en changer. */}
                        <LigneSaisie label="Téléphone">
                          <ChampTelephone value={form.telephone} style={inputStyle} ariaLabel="Téléphone" onChange={(v) => majF('telephone', v)} />
                        </LigneSaisie>
                        {/* ⚠️ L'INTITULÉ NE SE SAISIT PLUS (arbitrage RQ, 02/09/2026) :
                            il est CALCULÉ — rôles nominatifs · métier · pilotages et
                            suppléances. On montre le résultat et on nomme les endroits
                            où il se change : un champ grisé sans explication se lit
                            comme une panne. */}
                        <LigneSaisie label="Intitulé"
                          aide={<>Calculé : il suit les <strong>postes tenus</strong>, les <strong>pilotages</strong> et les rôles
                            d’organigramme — tous dans l’onglet <strong>Organisation</strong>.</>}>
                          <div style={{ fontSize: 13, padding: '8px 0' }}>{rh.intitule || rh.fonction || '—'}</div>
                        </LigneSaisie>
                      </>
                    ) : (
                      <>
                        <Ligne label="Prénom">{rh.prenom || vide}</Ligne>
                        <Ligne label="Nom">{rh.nom || vide}</Ligne>
                        <Ligne label="Initiales">
                          {rh.initiales
                            ? <span style={{ fontFamily: 'var(--font-mono)' }}>{rh.initiales}</span>
                            : <span style={{ color: 'var(--text-faint)' }}>— déduites de son nom ({initials(nomAff)})</span>}
                        </Ligne>
                        <Ligne label="Date de naissance">{jour(rh.dateNaissance) || vide}</Ligne>
                        <Ligne label="Adresse">{rh.adresse || vide}</Ligne>
                        <Ligne label="Courriel">
                          {rh.email || vide}
                          {adresseDivergente && (
                            <span style={{ display: 'block', color: 'var(--amber-600)', marginTop: 3 }}>
                              Le compte de connexion porte encore une autre adresse ({compte?.email}) :
                              enregistrer cet onglet aligne les deux.
                            </span>
                          )}
                        </Ligne>
                        <Ligne label="Téléphone">{rh.telephone || vide}</Ligne>
                        <Ligne label="Intitulé">{rh.intitule || rh.fonction || vide}</Ligne>
                      </>
                    )}
                  </section>
                  {/* La signature (image + texte) — apposée par MoMent à la validation biologique (11/10/2026). */}
                  {rh && <SignaturePersonne orgId={orgId} modifiable={peutEcrire || session.key === orgId} />}
                </div>

                <div style={TITRE_COL}>
                  {/* ---- Compte de connexion ----
                      Il est dans « Identité » et non dans une rubrique à lui :
                      l'identifiant de connexion est une des manières de nommer
                      la personne, à côté du numéro et des initiales. */}
                  {rh && (
                    <section style={{ ...card, padding: 16 }}>
                      <h3 style={SECTION}>Compte de connexion</h3>
                      {!compte ? (
                        <>
                          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
                            Cette personne <strong>n’a pas de compte</strong>, et ce n’est pas un manque : plus de
                            la moitié du personnel n’en a aucun. Elle reste planifiable, habilitable et payée —
                            mais elle ne recevra aucune diffusion et n’accusera aucune lecture.
                          </p>
                          {/* ⚠️ DEUX BOUTONS QUI SE TOUCHENT ET FONT L'INVERSE L'UN DE
                              L'AUTRE : les confondre donne DEUX comptes à une même
                              personne, dont un que nul ne réclamera. D'où la phrase
                              qui les sépare. */}
                          {isRQ && !enSaisie && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <RattacherCompte personne={rh} libelle="Relier à un compte"
                                onFait={() => { showToast('Compte rattaché.'); void loadRh(); }} />
                              <InviterCompte personne={rh} onFait={() => { void loadRh(); }} />
                            </div>
                          )}
                          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
                            {isRQ
                              ? <><strong>Relier</strong> si un compte existe déjà pour elle et que personne ne le
                                revendique. <strong>Inviter</strong> s’il n’en existe aucun : le compte est créé et
                                elle reçoit un lien pour choisir son mot de passe.</>
                              : <>Relier un compte existant à cette personne relève du <strong>responsable
                                qualité</strong> : le geste donne les accès d’un compte à quelqu’un de nommé, et le
                                journal attribuera ensuite ses actes à ce nom.</>}
                          </p>
                        </>
                      ) : (
                        <>
                          {/* ⚠️ LE SEUL CHAMP « IDENTIFIANT » DU DOSSIER (RQ, 03/10/2026 :
                              « il doit y avoir un seul champ pour l'identifiant »). C'est
                              `up_users.username`, ce qu'on TAPE à la connexion. Le numéro
                              P-000n (en tête) et la clé interne (`orgId`) ne s'appellent
                              plus « identifiant » nulle part : ni l'un ni l'autre n'ouvre
                              une session. */}
                          <Ligne label="Identifiant de connexion">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'var(--font-mono)', userSelect: 'all' }}>{compte.identifiant}</span>
                              {peutChangerIdentifiant && !enSaisie && (
                                <ChangerIdentifiant orgId={orgId} actuel={compte.identifiant} onFait={() => { onEnregistre?.(); }} />
                              )}
                              {/* Le DÉTACHEMENT est réservé à la RQ, comme le rattachement
                                  qu'il défait — et non ouvert à la RH, qui peut pourtant
                                  renommer l'identifiant juste à côté : renommer garde le
                                  titulaire, détacher le change. */}
                              {isRQ && !enSaisie && (
                                <DetacherCompte personne={rh} identifiant={compte.identifiant}
                                  onFait={() => { showToast('Compte détaché.'); void loadRh(); }} />
                              )}
                            </span>
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>
                              À la connexion, la personne tape cet identifiant <strong>ou</strong> son adresse de
                              courriel{compte.email ? <> (<span style={{ fontFamily: 'var(--font-mono)' }}>{compte.email}</span>)</> : null}.
                              {peutChangerIdentifiant ? ' Le responsable qualité, le gestionnaire RH et le super administrateur peuvent le modifier.' : ''}
                            </span>
                          </Ligne>
                          {/* `libelleProfil` et non `COMPTE_LABEL` : celui-ci nomme le
                              CYCLE DE VIE, pas le profil. */}
                          <Ligne label="Profil">{compte.profil ? libelleProfil(compte.profil) : vide}</Ligne>
                          <Ligne label="État">
                            <span style={{ color: etatCompte?.fg }}>{etatCompte?.label || compte.etat}</span>
                          </Ligne>
                          {compte.etat === 'invite' && (
                            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.55 }}>
                              Le compte existe mais ne permet <strong>aucune connexion</strong> tant que la personne
                              n’a pas choisi son mot de passe.
                              {compte.inviteExpires && <> Le lien expire le {jour(String(compte.inviteExpires))}.</>}
                              {' '}Si elle n’a rien reçu, l’invitation se renvoie depuis <strong>Paramétrage → Utilisateurs</strong>.
                            </p>
                          )}
                          {compte.etat === 'expiree' && (
                            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '8px 0 0', lineHeight: 1.55 }}>
                              Le lien d’invitation est mort : cette personne ne peut plus rien en faire. Renvoyez
                              l’invitation depuis <strong>Paramétrage → Utilisateurs</strong>.
                            </p>
                          )}
                          {compte.etat === 'actif' && compte.jamaisConnecte && (
                            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.55 }}>
                              Mot de passe choisi, mais aucune connexion à ce jour.
                            </p>
                          )}
                          {(isRQ || session.profile === 'superadmin') && (
                            <Ailleurs>
                              Les droits d’accès, la relance d’invitation et la suspension du compte se règlent
                              depuis{' '}
                              {/* L'annuaire n'est servi qu'au PROFIL super admin (le moteur
                                  refuse tout autre) : le lien n'est proposé qu'à lui.
                                  Hors saisie seulement : quitter le dossier abandonnerait un
                                  brouillon en cours sans passer par la garde de sortie. */}
                              {session.profile === 'superadmin' && !enSaisie
                                ? (
                                  <button type="button" onClick={() => { onClose(); openSettings('utilisateurs'); }}
                                    style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 700, color: 'var(--color-primary)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                                    Paramétrage → Utilisateurs
                                  </button>
                                )
                                : <strong>Paramétrage → Utilisateurs</strong>}
                              , la porte unique de l’annuaire des comptes.
                            </Ailleurs>
                          )}
                        </>
                      )}
                    </section>
                  )}
                </div>
              </div>
            )}

            {/* ════════ ONGLET 2 — ORGANISATION ════════ */}
            {onglet === 'organisation' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: 14, alignItems: 'start' }}>
                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Position dans l’organisation</h3>
                    {enSaisie === 'organisation' ? (
                      <>
                        <LigneSaisie label="Niveau">
                          <select value={form.orgTier} style={inputStyle}
                            onChange={(e) => majF('orgTier', e.target.value)}>
                            {/* Vide = « déduit de la fonction » (`tierOfUser`) : le
                                proposer évite d'imposer un niveau en ouvrant l'édition. */}
                            <option value="">Déduit de l’intitulé</option>
                            {ORG_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                          </select>
                        </LigneSaisie>
                        {/* Le site est une LISTE, même à une seule entrée (RQ,
                            01/09/2026) : un champ libre a laissé « Monastir — Ruspina »
                            s'écrire à la main vingt-trois fois. */}
                        <LigneSaisie label="Site">
                          <select value={form.site} style={inputStyle} onChange={(e) => majF('site', e.target.value)}>
                            {!form.site && <option value="">— aucun —</option>}
                            {(settings?.sites || []).map((x) => <option key={x} value={x}>{x}</option>)}
                            {/* Une valeur héritée hors paramétrage reste sélectionnable,
                                sinon l'édition la remplacerait en silence. */}
                            {form.site && !(settings?.sites || []).includes(form.site) && (
                              <option value={form.site}>{form.site} — hors paramétrage</option>
                            )}
                          </select>
                        </LigneSaisie>
                        <LigneSaisie label="Rattaché(e) à">
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 7 }}>
                            <select value={form.responsableOrgId} style={inputStyle}
                              onChange={(e) => majF('responsableOrgId', e.target.value)}>
                              <option value="">Racine de l’organigramme</option>
                              {encadrants.map((p) => (
                                <option key={p.orgId} value={p.orgId}>{nomDe(p)}</option>
                              ))}
                            </select>
                            <select value={form.lienResponsable} style={inputStyle} disabled={!form.responsableOrgId}
                              onChange={(e) => majF('lienResponsable', e.target.value)}>
                              <option value="hierarchique">Hiérarchique</option>
                              <option value="fonctionnel">Fonctionnel</option>
                            </select>
                          </div>
                        </LigneSaisie>
                        {/* Des listes à ajout/retrait et non un `<select multiple>` :
                            celui-ci exige Ctrl pour cumuler et un clic simple y
                            REMPLACE la sélection. */}
                        <LigneSaisie label="Postes tenus">
                          <SelecteurPastilles mono={false} modifiable unite="case"
                            ariaLabel="Cases de l’organigramme tenues par cette personne"
                            placeholder="Choisir une case de l’organigramme…"
                            vide="Aucune — la personne ne tient aucune case de l’organigramme fonctionnel."
                            options={(settings?.orgFonctions || []).map((f) => ({ documentId: f.id, code: f.label, nom: f.label }))}
                            choisis={form.fonctionIds}
                            onChange={(ids) => majF('fonctionIds', ids)} />
                          {/* ⚠️ Deux listes homonymes : dire laquelle fait quoi. */}
                          <div style={{ marginTop: 5, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                            Ces rôles composent l’<strong>intitulé affiché</strong>. Ils n’ouvrent <strong>aucun accès</strong> :
                            les droits se règlent dans Paramétrage → Rôles &amp; accès.
                          </div>
                        </LigneSaisie>
                      </>
                    ) : (
                      <>
                        <Ligne label="Niveau">{ORG_TIERS.find((t) => t.key === tierOfUser(u))?.label || vide}</Ligne>
                        <Ligne label="Site">{u.site || vide}</Ligne>
                        <Ligne label="Rattaché(e) à">
                          {responsable
                            ? <>{responsable.nom}{u.lien === 'fonctionnel' && <span style={{ color: 'var(--text-muted)' }}> · rattachement fonctionnel</span>}</>
                            : <span style={{ color: 'var(--text-muted)' }}>Racine de l’organigramme</span>}
                        </Ligne>
                        <Ligne label="Encadre">
                          {encadres.length ? `${encadres.length} personne(s) — ${encadres.map((e) => e.nom).join(', ')}` : vide}
                        </Ligne>
                        <Ligne label="Postes tenus">
                          {fonctionsTenues.length
                            ? <>
                              {fonctionsTenues.map((f) => f.label).join(' · ')}
                              {fonctionsTenues.length > 1 && (
                                <span style={{ color: 'var(--text-muted)' }}> — {fonctionsTenues.length} cases</span>
                              )}
                            </>
                            : vide}
                        </Ligne>
                      </>
                    )}
                    {/* « Encadre » n'a pas de champ : c'est le MIROIR des
                        rattachements des autres. */}
                  </section>
                </div>

                <div style={TITRE_COL}>
                  {/* ---- Rôles & responsabilités : une grille LIBELLÉ | VALEUR ---- */}
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Rôles &amp; responsabilités</h3>
                    <div style={GRILLE}>
                      <Rang label="Rôles documentaires (droits)">
                        {(u.roles || []).length
                          ? (u.roles || []).map((r) => GED_ROLES[r]?.label || r).join(' · ')
                          : <span style={{ color: 'var(--text-muted)' }}>Aucun — cette personne ne se connecte pas</span>}
                      </Rang>
                      {/* ⚠️ HOMONYMIE LEVÉE (lot L4, 10/09/2026) : les clés `rq` et
                          `rh` désignent ici un INTITULÉ, et au-dessus un DROIT.
                          « Responsable qualité » coché ici ne donne AUCUN accès. Les
                          CLÉS ne sont pas renommées (figées dans le journal chaîné). */}
                      <Rang label="Rôles d’organigramme (intitulé)">
                        {enSaisie === 'organisation' ? (
                          <SelecteurPastilles mono={false} modifiable unite="rôle"
                            ariaLabel="Rôles d’organigramme de cette personne"
                            placeholder="Choisir un rôle…"
                            vide="Aucun — l’intitulé se composera de la fonction métier seule."
                            options={Object.entries(ROLES_ORG).map(([cle, libelle]) => ({ documentId: cle, code: String(libelle), nom: String(libelle) }))}
                            choisis={form.rolesOrg}
                            onChange={(cles) => majF('rolesOrg', cles)} />
                        ) : ((rh?.rolesOrg || []).length
                          ? (rh?.rolesOrg || []).map((r) => ROLES_ORG[r] || r).join(' · ')
                          : vide)}
                      </Rang>
                      {/* Pilote / suppléant : la MÊME donnée que `/processus`, éditée
                          depuis l'autre bout (spec §Q5). Choisir quelqu'un ici DÉPLACE
                          le pilotage — qui le tenait avant est nommé sous le champ. */}
                      <Rang label="Pilote de">
                        {enSaisie === 'organisation' ? (
                          <>
                            <SelecteurPastilles mono={false} modifiable unite="processus"
                              ariaLabel="Processus pilotés par cette personne"
                              placeholder="Choisir un processus…"
                              vide="Aucun — cette personne ne pilote aucun processus."
                              options={processus.filter((x) => x.active !== false).map((x) => ({ documentId: x.id, code: x.name, nom: x.name }))}
                              choisis={form.pilotages}
                              onChange={(ids) => majF('pilotages', ids)} />
                            <DeplacementsProcessus titulaires={processus.filter((x) => x.active !== false)
                              .filter((x) => form.pilotages.includes(x.id) && x.pilote && x.pilote !== orgId)
                              .map((x) => ({ nom: x.name, qui: nomDe(rhPersonnel.find((r) => r.orgId === x.pilote)) }))}
                              verbe="piloté" />
                          </>
                        ) : (pilote.length ? pilote.map((p) => p.name).join(' · ') : vide)}
                      </Rang>
                      <Rang label="Suppléant de">
                        {enSaisie === 'organisation' ? (
                          <>
                            <SelecteurPastilles mono={false} modifiable unite="processus"
                              ariaLabel="Processus suppléés par cette personne"
                              placeholder="Choisir un processus…"
                              vide="Aucun — cette personne ne supplée aucun processus."
                              options={processus.filter((x) => x.active !== false).map((x) => ({ documentId: x.id, code: x.name, nom: x.name }))}
                              choisis={form.suppleances}
                              onChange={(ids) => majF('suppleances', ids)} />
                            <DeplacementsProcessus titulaires={processus.filter((x) => x.active !== false)
                              .filter((x) => form.suppleances.includes(x.id) && x.suppleant && x.suppleant !== orgId)
                              .map((x) => ({ nom: x.name, qui: nomDe(rhPersonnel.find((r) => r.orgId === x.suppleant)) }))}
                              verbe="suppléé" />
                          </>
                        ) : (supplee.length ? supplee.map((p) => p.name).join(' · ') : vide)}
                      </Rang>
                    </div>
                    {isRQ && !enSaisie && (
                      <Ailleurs>
                        Les droits d’accès se règlent depuis <strong>Paramétrage → Rôles &amp; accès</strong> — le
                        rôle « gestionnaire RH » ouvre à lui seul la paie et les dossiers de tout le monde.
                      </Ailleurs>
                    )}
                  </section>
                </div>
              </div>
            )}

            {/* ════════ ONGLET 3 — CONTRAT & ADMINISTRATIF ════════ */}
            {onglet === 'contrat' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: 14, alignItems: 'start' }}>
                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Administratif</h3>
                    {!voitAdmin ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '10px 0 0', lineHeight: 1.5 }}>
                        Matricule, badge, RIB et dates ne sont visibles que par le <strong>gestionnaire RH</strong> et
                        le <strong>responsable qualité</strong>.
                      </p>
                    ) : enSaisie === 'contrat' ? (
                      <div style={{ marginTop: 4 }}>
                        {/* ⚠️ DEUX NUMÉROS, et ils ne se ressemblent que de loin —
                            celui de la BADGEUSE et celui du CABINET COMPTABLE.
                            Chaque champ dit lequel il est : c'est la seule chose
                            qui empêche de reporter l'un à la place de l'autre.
                            L'ancien champ « Matricule » a été RETIRÉ le 03/10/2026
                            (RQ) : il portait le même numéro de badgeuse que
                            « Badge » (18 fiches sur 18), et les 4 dernières
                            arrivées n'avaient plus que le second. */}
                        {([
                          ['badgeId', 'N° de badge', 'text',
                            'Le numéro de la badgeuse — celui qui figure sur le badge, pas celui de la paie. Refusé s’il est déjà attribué : partagé, il porterait les heures de l’un au compte de l’autre.'],
                          ['matriculePaie', 'Matricule de paie', 'text',
                            'Celui du cabinet comptable, sans rapport avec le badge. Sans lui, la ligne de paie sort avec une réserve — jamais avec le numéro de badge à sa place.'],
                          ['coefficientGarde', 'Coefficient de garde', 'text',
                            'Le multiplicateur de la colonne « Variable » du récapitulatif comptable : Variable = gardes × coefficient (ex. 4,25). Vide : « coefficient à fixer » dans le récapitulatif.'],
                          ['rib', 'RIB (20 chiffres)', 'text',
                            'Refusé si sa clé de contrôle ne tombe pas juste. Une clé valide ne prouve pourtant rien : elle attrape une faute de frappe sur 97, pas la permutation de deux comptes existants. À reconfronter au document de la banque.'],
                          ['banque', 'Banque', 'text', null],
                          ['dateEntree', 'Date d’entrée', 'date', null],
                          ['dateSortie', 'Date de sortie', 'date',
                            'Une date passée retire la personne de l’organigramme le jour venu, sans autre geste.'],
                        ] as const).map(([k, label, type, aide]) => (
                          <LigneSaisie key={k} label={label} aide={aide}>
                            <input type={type} value={form[k]} style={inputStyle}
                              onChange={(e) => majF(k, e.target.value)} />
                          </LigneSaisie>
                        ))}
                        {/* ── LE POINTAGE : soumis ou non. ⚠️ Distinct de l'absence de
                            badge : ici la personne EN A un et l'on déclare qu'elle
                            n'a pas à s'en servir. */}
                        <LigneSaisie label="Pointage">
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input type="checkbox" checked={form.soumisAuPointage}
                              onChange={(e) => majF('soumisAuPointage', e.target.checked)} />
                            Soumis(e) au badgeage
                          </label>
                        </LigneSaisie>
                        <LigneSaisie label="Congés" aide="Décoché : la personne n’a pas de compteur de jours de congés (co-direction, RQ, comptable). Ses absences restent enregistrées.">
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input type="checkbox" checked={form.soumisAuxConges}
                              onChange={(e) => majF('soumisAuxConges', e.target.checked)} />
                            Soumis(e) aux jours de congés
                          </label>
                        </LigneSaisie>
                        {!form.soumisAuPointage && (
                          <p style={{ fontSize: 12.5, color: 'var(--amber-600)', background: 'var(--amber-50)',
                            borderRadius: 'var(--radius-sm)', padding: '8px 10px', margin: '6px 0 0', lineHeight: 1.5 }}>
                            Aucune vacation ne sera attendue de cette personne : elle ne sera jamais comptée
                            absente faute de badge. Ses passages éventuels resteront visibles, hors vacation —
                            rien n’est effacé.
                          </p>
                        )}
                        {form.soumisAuPointage && (
                          <LigneSaisie label="Inscrit(e) à la badgeuse depuis"
                            aide="Avant cette date, ses jours planifiés sont « non mesurés » — ni présence, ni absence. Vide : depuis toujours.">
                            <input type="date" value={form.pointageDepuis} style={inputStyle}
                              onChange={(e) => majF('pointageDepuis', e.target.value)} />
                          </LigneSaisie>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 4 }}>
                        <Ligne label="N° de badge">{rh?.badgeId || vide}</Ligne>
                        <Ligne label="Matricule de paie">{rh?.matriculePaie || vide}</Ligne>
                        <Ligne label="Coefficient de garde">{rh?.coefficientGarde != null ? String(rh.coefficientGarde).replace('.', ',') : vide}</Ligne>
                        <Ligne label="RIB">
                          {rh?.rib
                            ? <span style={{ fontFamily: 'var(--font-mono)' }}>{ribLisible(rh.rib)}</span>
                            : vide}
                          {rh?.banque ? <span style={{ color: 'var(--text-muted)' }}> · {rh.banque}</span> : null}
                        </Ligne>
                        <Ligne label="Pointage">
                          {rh?.soumisAuPointage === false
                            ? <span style={{ color: 'var(--amber-600)' }}>Non soumis(e) au badgeage</span>
                            : 'Soumis(e) au badgeage'}
                          {rh?.soumisAuPointage !== false && rh?.pointageDepuis
                            ? <span style={{ color: 'var(--text-muted)' }}> · inscrit(e) à la badgeuse le {jour(rh.pointageDepuis)}</span>
                            : null}
                        </Ligne>
                        <Ligne label="Congés">
                          {rh?.soumisAuxConges === false
                            ? <span style={{ color: 'var(--text-muted)' }}>Sans compteur de jours de congés</span>
                            : 'Soumis(e) aux jours de congés'}
                        </Ligne>
                        <Ligne label="Entrée">{jour(rh?.dateEntree) || vide}</Ligne>
                        <Ligne label="Sortie">{jour(rh?.dateSortie) || vide}</Ligne>
                        <Ligne label="Nature">{rh?.type === 'externe' ? 'Externe / correspondant' : 'Salarié(e)'}</Ligne>
                      </div>
                    )}
                  </section>
                </div>

                <div style={TITRE_COL}>
                  {/* ---- Contrats ----
                      ⚠️ ILS ONT LEUR PROPRE FORMULAIRE, et donc leur propre
                      « Enregistrer le contrat » : un contrat est un
                      ENREGISTREMENT À PART (`rh-contrat`), daté, qui se clôt et
                      se remplace — pas un champ de la fiche. Il ne part donc
                      jamais avec le pied de l'onglet. */}
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Contrats</h3>
                    {!voitAdmin ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0, lineHeight: 1.5 }}>
                        Les contrats ne sont visibles que par le <strong>gestionnaire RH</strong> et
                        le <strong>responsable qualité</strong>.
                      </p>
                    ) : (
                      <>
                        {contrats === null && <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>Chargement…</div>}
                        {contrats?.length === 0 && (
                          <div style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                            Aucun contrat enregistré — sans lui, le module ne peut pas séparer heures normales et supplémentaires.
                          </div>
                        )}
                        {(contrats || []).map((c) => (
                          <div key={c.documentId} style={{ fontSize: 12.5, color: 'var(--text-body)', padding: '6px 0', borderBottom: '1px solid var(--border-default)', display: 'grid', gap: 5 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ flex: 1 }}>
                                <strong>{(c.contratType || '').toUpperCase()}</strong>
                                {' · '}{jour(c.dateDebut) || '?'} → {jour(c.dateFin) || 'en cours'}
                                {' · '}{c.regimeHebdo} h/sem, quotité {c.quotite}%
                                {c.joursCongesAnnuels ? ` · ${c.joursCongesAnnuels} j de congé` : ' · droit à congé non renseigné'}
                                {!c.actif && <span style={{ color: 'var(--text-faint)' }}> · clos</span>}
                              </span>
                              {peutEcrire && c.actif && !contratEdite && !enSaisie && (
                                <Bouton taille="sm" type="button" aria-label="Modifier ce contrat" title="Modifier ce contrat" onClick={() => ouvrirContrat(c)}>
                                  <I n="pen" s={11} />
                                </Bouton>
                              )}
                            </div>
                            {/* LE CONTRAT SIGNÉ (RQ, 02/10/2026) : pièce jointe au
                                contrat, téléchargeable et retirable. Chaque ouverture
                                est tracée par le serveur. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {c.piece ? (
                                <>
                                  <Bouton taille="sm" type="button" onClick={() => void ouvrirPieceContrat(c.documentId, c.piece?.nom || 'contrat')} title="Télécharger le contrat signé">
                                    <I n="doc" s={11} />{c.piece.nom}
                                  </Bouton>
                                  <span style={{ color: 'var(--text-muted)' }}>{poids(c.piece.taille)}{c.piece.date ? ` · déposé le ${jour(c.piece.date)}` : ''}</span>
                                  {peutEcrire && (
                                    /* Suppression IRRÉVERSIBLE du fichier : danger + confirmation (elle n'en avait aucune). */
                                    <ConfirmButton label="" icon="trash" variant="danger" disabled={busy}
                                      titre="Supprimer le contrat signé"
                                      question="Supprimer le contrat signé joint ? Le fichier ne se récupère pas."
                                      confirmer="Supprimer"
                                      onConfirm={() => retirerDuContrat(c.documentId)} />
                                  )}
                                </>
                              ) : (
                                <span style={{ color: 'var(--text-faint)' }}>Aucun contrat signé joint</span>
                              )}
                              {peutEcrire && !enSaisie && (
                                <label className="bio-btn bio-btn--contour" style={{ ...styleBouton('contour', 'sm'), cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.45 : 1 }}>
                                  <I n="plus" s={11} />{c.piece ? 'Remplacer' : 'Joindre le contrat signé'}
                                  <input type="file" accept="application/pdf,image/*" hidden disabled={busy}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0]; e.target.value = '';
                                      if (f) void joindreAuContrat(c.documentId, f);
                                    }} />
                                </label>
                              )}
                            </div>
                          </div>
                        ))}
                        {/* ⚠️ LA SAISIE MANQUAIT ENTIÈREMENT (arbitrage RQ, 02/09/2026) :
                            `POST /ged/rh/contrats` n'était appelé par personne. C'est
                            l'ÉTAPE 1 du parcours d'intégration — sans contrat, aucun
                            solde de congé n'est calculable. */}
                        {/* ⚠️ RIEN NE S'ÉCRIT ICI PENDANT QUE LES CHAMPS DE L'ONGLET
                            SONT EN SAISIE (`!enSaisie`) — ni contrat, ni pièce.
                            Les deux saisies pouvaient s'ouvrir en même temps : deux
                            pieds « Enregistrer » à l'écran, dont un collé en bas qui
                            ne portait pas le contrat, et aucun des deux ne disait
                            lequel. Et joindre une pièce recharge la liste des
                            contrats, donc re-rend la carte sous le brouillon ouvert.
                            La saisie du contrat, elle, reste possible hors édition de
                            l'onglet : c'est un enregistrement à part. */}
                        {peutEcrire && !contratEdite && !enSaisie && (
                          <Bouton taille="sm" type="button" onClick={() => ouvrirContrat(null)} style={{ marginTop: 6 }}>
                            <I n="plus" s={12} />{contrats?.length ? 'Nouveau contrat' : 'Enregistrer son contrat'}
                          </Bouton>
                        )}
                        {contratEdite && (
                          <div style={{ marginTop: 10, display: 'grid', gap: 8, padding: '10px 12px', background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)' }}>
                            <LigneSaisie label="Type">
                              <select value={contratEdite.contratType} style={inputStyle}
                                onChange={(e) => majContrat('contratType', e.target.value)}>
                                {TYPES_CONTRAT.map((t) => (
                                  <option key={t.valeur} value={t.valeur}>{t.intitule}</option>
                                ))}
                                {/* Un type repris en base hors liste doit rester visible :
                                    sans cette option, le <select> afficherait le premier
                                    type et le REMPLACERAIT en silence. */}
                                {contratEdite.contratType
                                  && !TYPES_CONTRAT.some((t) => t.valeur === contratEdite.contratType) && (
                                  <option value={contratEdite.contratType}>{contratEdite.contratType}</option>
                                )}
                              </select>
                            </LigneSaisie>
                            <LigneSaisie label="Début">
                              <input type="date" value={contratEdite.dateDebut} style={inputStyle}
                                onChange={(e) => majContrat('dateDebut', e.target.value)} />
                            </LigneSaisie>
                            <LigneSaisie label="Fin">
                              <input type="date" value={contratEdite.dateFin} style={inputStyle}
                                onChange={(e) => majContrat('dateFin', e.target.value)} />
                            </LigneSaisie>
                            <LigneSaisie label="Régime">
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <input type="number" min={1} max={84} value={contratEdite.regimeHebdo} style={{ ...inputStyle, width: 90 }}
                                  onChange={(e) => majContrat('regimeHebdo', e.target.value)} />
                                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>heures par semaine</span>
                              </span>
                            </LigneSaisie>
                            <LigneSaisie label="Quotité">
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <input type="number" min={1} max={100} value={contratEdite.quotite} style={{ ...inputStyle, width: 90 }}
                                  onChange={(e) => majContrat('quotite', e.target.value)} />
                                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>%</span>
                              </span>
                            </LigneSaisie>
                            <LigneSaisie label="Congés annuels">
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <input type="number" min={0} max={60} value={contratEdite.joursCongesAnnuels} style={{ ...inputStyle, width: 90 }}
                                  onChange={(e) => majContrat('joursCongesAnnuels', e.target.value)} />
                                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>jours — c’est lui qui porte le solde</span>
                              </span>
                            </LigneSaisie>
                            <LigneSaisie label="Heures sup.">
                              <select value={contratEdite.traitementHeuresSup} style={inputStyle}
                                onChange={(e) => majContrat('traitementHeuresSup', e.target.value)}>
                                <option value="repos">Récupérées en repos</option>
                                <option value="remuneration">Rémunérées</option>
                                <option value="aucun">Ni l’un ni l’autre</option>
                              </select>
                            </LigneSaisie>
                            <LigneSaisie label="Contrat signé"
                              aide="Facultatif — PDF ou image. Il se joint aussi plus tard, depuis la ligne du contrat.">
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <label className="bio-btn bio-btn--contour" style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer' }}>
                                  <I n="plus" s={12} />{contratFichier ? 'Changer le fichier' : 'Choisir un fichier'}
                                  <input type="file" accept="application/pdf,image/*" hidden
                                    onChange={(e) => { setContratFichier(e.target.files?.[0] || null); e.target.value = ''; }} />
                                </label>
                                {contratFichier && (
                                  <>
                                    <span style={{ fontSize: 12.5 }}>{contratFichier.name} · {poids(contratFichier.size)}</span>
                                    <Bouton taille="sm" type="button" aria-label="Ne pas joindre ce fichier" onClick={() => setContratFichier(null)}><I n="x" s={11} /></Bouton>
                                  </>
                                )}
                              </div>
                            </LigneSaisie>
                            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                              {contratEdite.documentId
                                ? 'Modification du contrat en cours.'
                                : contrats?.length
                                  ? 'Un nouveau contrat REMPLACE le contrat actif : l’ancien reste lisible, il n’est pas réécrit.'
                                  : 'Sans contrat, le module ne peut ni décompter les congés ni séparer heures normales et supplémentaires.'}
                            </p>
                            <PiedActions taille="sm" annuler={{ onClick: () => { setContratEdite(null); setContratFichier(null); } }} principale={{ libelle: 'Enregistrer le contrat', libelleChargement: 'Enregistrement…', chargement: busy, disabled: busy || !contratEdite.dateDebut, title: !contratEdite.dateDebut ? 'La date de début est obligatoire.' : undefined, onClick: () => void enregistrerLeContrat() }} />
                          </div>
                        )}
                      </>
                    )}
                  </section>
                </div>
              </div>
            )}

            {/* ════════ ONGLET 4 — COMPÉTENCES & FORMATION ════════
                EN LECTURE, ENTIÈREMENT. Une habilitation porte un niveau, une
                origine et une date — c'est la preuve qu'un auditeur demande, et
                elle ne se coche pas. Cet onglet RASSEMBLE ce que trois registres
                écrivent ; il nomme chacun. */}
            {onglet === 'competences' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: 14, alignItems: 'start' }}>
                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Habilitations · ISO 15189 §6.2</h3>
                    {!compétences.length ? (
                      <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>
                        Aucune habilitation enregistrée. Cette personne ne peut ni vérifier ni approuver de document.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                        {habilite.map((d) => (
                          <span key={d} style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: 'var(--green-50)', color: 'var(--green-600)' }}>{d}</span>
                        ))}
                        {/* La formation est montrée AUTREMENT, pas juste d'une autre
                            couleur : elle ne vaut pas habilitation à signer. */}
                        {enFormation.map((d) => (
                          <span key={d} style={{ fontSize: 11, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>{d} — en formation</span>
                        ))}
                      </div>
                    )}
                    <Ailleurs>
                      Les habilitations s’accordent depuis <strong>Habilitations — référentiel</strong>, onglet
                      « Habilitations par personne » : chacune porte un niveau, une origine et une date, parce que
                      c’est la preuve qu’un auditeur demande.
                    </Ailleurs>
                  </section>

                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Postes qu’elle tient</h3>
                    <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 7px', lineHeight: 1.5 }}>
                      Les cases de l’organigramme fonctionnel composent son intitulé. Ce qu’elle est
                      <strong> habilitée à tenir</strong> est une autre question : elle se lit dans
                      « Compétences — qui tient quel poste ».
                    </p>
                    <div style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>
                      {fonctionsTenues.length ? fonctionsTenues.map((f) => f.label).join(' · ') : vide}
                    </div>
                  </section>
                </div>

                <div style={TITRE_COL}>
                  {/* ---- Parcours d'intégration ---- */}
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Parcours d’intégration</h3>
                    {!integration ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                        Aucun parcours ouvert. Ce n’est un manque que pour une arrivée récente : les
                        parcours n’existent dans le module que depuis 2026, et le personnel en poste
                        avant n’en a pas.
                      </p>
                    ) : (
                      <>
                        <Ligne label="Ouvert le">{jour(integration.dateDebut) || vide}</Ligne>
                        <Ligne label="État">
                          {integration.statut === 'cloture'
                            ? <span style={{ color: 'var(--green-600)' }}>Clôturé{integration.clotureLe ? ` le ${jour(integration.clotureLe)}` : ''}{integration.cloturePar ? ` par ${integration.cloturePar}` : ''}</span>
                            : <span style={{ color: 'var(--amber-600)' }}>En cours</span>}
                        </Ligne>
                        {/* Le COMPTE des activités faites, jamais la liste : elle
                            fait cent quarante lignes et vit dans son écran. */}
                        <Ligne label="Activités">
                          {(() => {
                            const toutes = (integration.phases || []).flatMap((p) => p.activites || []);
                            if (!toutes.length) return vide;
                            const faites = toutes.filter((a) => a.fait).length;
                            return `${faites} / ${toutes.length} faites`;
                          })()}
                        </Ligne>
                        <Ligne label="Habilitations au programme">
                          {(integration.habilitations || []).length
                            ? `${(integration.habilitations || []).filter((h) => h.acquise).length} acquise(s) sur ${(integration.habilitations || []).length}`
                            : vide}
                        </Ligne>
                        {!!integration.conclusion && (
                          <p style={{ fontSize: 12.5, color: 'var(--text-body)', margin: '8px 0 0', lineHeight: 1.55 }}>
                            {integration.conclusion}
                          </p>
                        )}
                      </>
                    )}
                    <Ailleurs>
                      Le parcours se conduit depuis <strong>Habilitations — référentiel</strong>, onglet
                      « Parcours d’intégration » : c’est là que les activités se cochent et que le dossier se clôt.
                    </Ailleurs>
                  </section>

                  {/* ---- Parcours documentaire ---- */}
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Parcours documentaire</h3>
                    {/* Volontairement TOUS les documents, obsolètes compris : le
                        parcours d'une personne ne se réduit pas à ce qui est encore
                        applicable. */}
                    <Ligne label="A rédigé">{aRediges.length ? `${aRediges.length} document(s) — ${aRediges.map((d) => d.code).join(', ')}` : vide}</Ligne>
                    <Ligne label="A vérifié">{aVerifies.length ? `${aVerifies.length} — ${aVerifies.map((d) => d.code).join(', ')}` : vide}</Ligne>
                    <Ligne label="A approuvé">{aApprouves.length ? `${aApprouves.length} — ${aApprouves.map((d) => d.code).join(', ')}` : vide}</Ligne>
                  </section>
                </div>
              </div>
            )}

            {/* ════════ ONGLET 5 — TEMPS & ABSENCES ════════
                ⚠️ RIEN N'EST RECALCULÉ ICI. Le droit à congé dépend d'un mode
                paramétrable (fixe, mensuel, à l'ancienneté) : il vient du
                moteur avec SON explication, et `incomplet` dit quand il manque
                une donnée. Le refaire à l'écran l'aurait figé sur un mode. */}
            {onglet === 'temps' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: 14, alignItems: 'start' }}>
                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Congés</h3>
                    {rh?.soumisAuxConges === false ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
                        Cette personne n’a <strong>pas de compteur</strong> de jours de congés — c’est une
                        décision portée par son dossier (onglet « Contrat &amp; administratif »), pas un
                        calcul manquant. Ses absences restent enregistrées ci-contre.
                      </p>
                    ) : !solde ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0 }}>
                        Compteur indisponible pour le moment.
                      </p>
                    ) : (
                      <>
                        <Ligne label="Droit annuel">{solde.droit} j</Ligne>
                        <Ligne label="Report">{solde.report} j</Ligne>
                        <Ligne label="Pris">{solde.pris} j</Ligne>
                        <Ligne label="Solde"><strong>{solde.solde} j</strong></Ligne>
                        {/* `incomplet` dit que le calcul manque d'une donnée — le
                            taire ferait passer un chiffre partiel pour un chiffre sûr. */}
                        <p style={{ fontSize: 12, margin: '8px 0 0', lineHeight: 1.5,
                          color: solde.incomplet ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                          {solde.incomplet ? '⚠ ' : ''}{solde.explication}
                        </p>
                      </>
                    )}
                  </section>

                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Temps de travail</h3>
                    {!contratActif ? (
                      <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.5 }}>
                        Aucun contrat actif : le module ne peut ni décompter les congés ni séparer heures
                        normales et supplémentaires. Il s’enregistre dans l’onglet « Contrat &amp; administratif ».
                      </p>
                    ) : (
                      <>
                        <Ligne label="Régime">{contratActif.regimeHebdo != null ? `${contratActif.regimeHebdo} h par semaine` : vide}</Ligne>
                        <Ligne label="Quotité">{contratActif.quotite != null ? `${contratActif.quotite} %` : vide}</Ligne>
                        <Ligne label="Heures sup.">
                          {contratActif.traitementHeuresSup === 'repos' ? 'Récupérées en repos'
                            : contratActif.traitementHeuresSup === 'remuneration' ? 'Rémunérées'
                              : contratActif.traitementHeuresSup === 'aucun' ? 'Ni l’une ni l’autre' : vide}
                        </Ligne>
                        <Ligne label="Badgeage">
                          {rh?.soumisAuPointage === false
                            ? <span style={{ color: 'var(--amber-600)' }}>Non soumis(e)</span>
                            : 'Soumis(e)'}
                        </Ligne>
                      </>
                    )}
                    {/* Le détail des journées pointées vit dans « Présences » : il
                        se lit par mois, avec ses corrections et ses motifs d'écart —
                        un extrait ici aurait laissé croire à un relevé complet. */}
                    {!enSaisie && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        {session.sections?.presences?.voir && (
                          <Bouton variante="discret" taille="sm" onClick={() => allerVers('presences')}>
                            Ses présences, mois par mois →
                          </Bouton>
                        )}
                        {/* ⚠️ `peutLirePlanning` ET NON LA CONDITION À LA MAIN :
                            la règle a été dupliquée quatre fois le 11/09/2026 et
                            deux copies avaient dérivé vers le seul rôle `rh`,
                            renvoyant une grille vide qui se lisait « personne
                            n'est planifié ». Le socle est la seule réponse, et
                            `front-acces-planning` refuse la cinquième copie. */}
                        {peutLirePlanning(rhAcces, session.sections) && (
                          <Bouton variante="discret" taille="sm" onClick={() => allerVers('planning')}>
                            Le planning →
                          </Bouton>
                        )}
                      </div>
                    )}
                  </section>
                </div>

                <div style={TITRE_COL}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Absences de l’année</h3>
                    {!absences.length ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                        Aucune absence enregistrée sur l’année civile en cours.
                      </p>
                    ) : (
                      <div style={{ display: 'grid' }}>
                        {absences.map((a) => {
                          const st = STATUT_ABSENCE[a.statut] || { label: a.statut, fg: 'var(--text-muted)' };
                          return (
                            <div key={a.documentId} style={{ padding: '7px 0', borderTop: '1px solid var(--border-default)', fontSize: 12.5 }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>
                                  {jour(a.dateDebut) || '?'}{a.dateFin && a.dateFin !== a.dateDebut ? ` → ${jour(a.dateFin)}` : ''}
                                </span>
                                <span style={{ color: st.fg, fontWeight: 600 }}>{st.label}</span>
                                {a.joursDecomptes != null && a.decompteSolde && (
                                  <span style={{ color: 'var(--text-muted)' }}>{a.joursDecomptes} j décompté(s)</span>
                                )}
                              </div>
                              {/* ⚠️ `motifLabel` SEUL, jamais `motif` : le moteur
                                  n'envoie le motif qu'à qui a le droit de le lire, et
                                  le libellé est sa rédaction publiable. Afficher l'un
                                  pour l'autre ferait sortir une donnée de santé. */}
                              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{a.motifLabel || '—'}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!enSaisie && session.sections?.absences?.voir && (
                      <Bouton variante="discret" taille="sm" style={{ marginTop: 10 }} onClick={() => allerVers('absences')}>
                        Décider et corriger dans « Absences &amp; congés » →
                      </Bouton>
                    )}
                  </section>
                </div>
              </div>
            )}

            {/* ════════ ONGLET 6 — PIÈCES ════════
                Pièces et journal ont deux onglets (RQ, 22/10/2026) : l'un se
                manipule, l'autre se lit. */}
            {onglet === 'pieces' && (
              <div style={{ ...TITRE_COL, maxWidth: 860 }}>
                  {/* ── LES PIÈCES SE GÈRENT ICI (RQ, 06/10 et 08/10/2026) ──────
                      « Je voudrais que le document soit ici pour modifier ou rajouter
                      des documents si besoin (bien que l'intégration soit terminée). »

                      ⚠️ PLUS AUCUNE CONDITION SUR LE PARCOURS. Jusqu'au 08/10, une
                      pièce était rattachée au dossier d'intégration : sans parcours —
                      c'est-à-dire pour tout le personnel entré avant 2026 — cet onglet
                      ne pouvait que constater qu'il n'y avait nulle part où classer.
                      Les pièces visent maintenant la PERSONNE, et tout le monde en a.

                      Le bloc est le MÊME composant que celui du parcours d'intégration
                      (`@/components/PiecesDossier`), pas une copie : une seconde
                      version aurait fini par ne plus dire que chaque consultation
                      laisse une trace nominative.

                      ⚠️ `modifiable` SUR `aRh` ET NON `peutEcrire` : dépôt, lecture et
                      retrait exigent le rôle `rh` côté moteur (`exigerRh`), là où le
                      reste du dossier s'ouvre au RQ. Proposer les boutons à un RQ sans
                      ce rôle donnerait un 403 à chaque clic. */}
                  {!aRh ? (
                    <section style={{ ...card, padding: 16 }}>
                      <h3 style={SECTION}>Pièces du dossier</h3>
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0, lineHeight: 1.55 }}>
                        Les pièces du dossier — CIN, photo, diplômes, RIB — relèvent du
                        <strong> gestionnaire RH</strong> : leur contenu ne sort que sur une trace
                        nominative, et le moteur n’en sert pas la liste aux autres comptes.
                      </p>
                    </section>
                  ) : pieces === null ? (
                    /* ⚠️ PAS D'ÉTAT VIDE TANT QU'ON NE SAIT PAS. « Aucune pièce »
                       est une AFFIRMATION : l'écrire avant d'avoir lu la ferait
                       passer pour vraie sur un dossier qui en porte neuf. */
                    <section style={{ ...card, padding: 16 }}>
                      <h3 style={SECTION}>Pièces du dossier</h3>
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0 }}>Lecture des pièces…</p>
                    </section>
                  ) : (
                    <BlocPieces
                      pieces={pieces}
                      clos={integration?.statut === 'cloture'}
                      /* ⚠️ REDONDANT AVEC LA BRANCHE `!aRh` CI-DESSUS, ET POSÉ
                         QUAND MÊME. Le droit est écrit À L'ENDROIT OÙ LES BOUTONS
                         SONT DEMANDÉS : le jour où la branche du dessus change de
                         condition — pour afficher autre chose à un RQ, par
                         exemple — elle n'emporterait pas silencieusement
                         l'ouverture de l'écriture avec elle. Un défaut à `true`
                         hérité d'une garde lointaine est exactement ce qui ouvre
                         une porte sans qu'on s'en aperçoive. */
                      modifiable={aRh}
                      onDeposer={(cle, libelle, f) => void deposerPiece(orgId, cle, libelle, f).then(relirePieces)}
                      onConsulter={consulterPiece}
                      onSupprimer={(id) => void supprimerPiece(id).then(relirePieces)} />
                  )}
              </div>
            )}

            {/* ════════ ONGLET 7 — JOURNAL ════════ */}
            {onglet === 'journal' && (
              <div style={{ maxWidth: 860 }}>
                  <section style={{ ...card, padding: 16 }}>
                    <h3 style={SECTION}>Ce que le journal retient d’elle</h3>
                    {!voitJournal ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0, lineHeight: 1.5 }}>
                        Le journal RH est une rubrique à part, réservée : il porte les consultations de
                        justificatifs et les clôtures de paie.
                      </p>
                    ) : journalPersonne === null ? (
                      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0 }}>Lecture du journal…</p>
                    ) : (
                      <>
                        {/* ⚠️ LA RÉSERVE EST AU-DESSUS DE LA LISTE, pas sous elle :
                            c'est une liste qu'on ne doit pas lire comme exhaustive,
                            et on l'apprend avant de la parcourir, pas après. */}
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
                          Relevé par <strong>rapprochement du libellé</strong> — le journal est chaîné et signé,
                          pas relationnel : ses actes ne portent pas de champ « personne ». Un acte peut donc
                          manquer ici. Le registre complet est dans <strong>Journal RH</strong>.
                        </p>
                        {!journalPersonne.length ? (
                          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
                            Aucun acte ne la nomme dans le journal lu.
                          </p>
                        ) : (
                          <div style={{ display: 'grid' }}>
                            {journalPersonne.map((e) => (
                              <div key={e.documentId} style={{ padding: '7px 0', borderTop: '1px solid var(--border-default)', fontSize: 12.5 }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
                                    {jour(jourIso(e.ts)) || ''}
                                  </span>
                                  <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{e.evType}</span>
                                  <span style={{ color: 'var(--text-muted)' }}>par {e.actorName || e.actor}</span>
                                </div>
                                <div style={{ color: 'var(--text-body)', marginTop: 2, lineHeight: 1.5 }}>{e.payload}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!enSaisie && (
                          <Bouton variante="discret" taille="sm" style={{ marginTop: 10 }} onClick={() => allerVers('rhjournal')}>
                            Le journal RH complet →
                          </Bouton>
                        )}
                      </>
                    )}
                  </section>
              </div>
            )}

            <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 16, lineHeight: 1.55 }}>
              Archiver ou suspendre bloque l’accès et retire la personne des affectations à venir.
              <strong> Rien n’est supprimé</strong> : documents, signatures et journaux gardent leur auteur (ISO 15189 §8.3).
              Les responsabilités en cours — pilotages, suppléances, encadrements — se transfèrent
              depuis l’<strong>organigramme</strong>, où le panneau se remplit au fil des choix.
            </p>
          </div>

          {/* ---- LE PIED, COLLÉ EN BAS (convention §5) ----
              ⚠️ ICI ET NON DANS L'EN-TÊTE. « Enregistrer la fiche » était posé en
              haut, au-dessus d'un corps qui défile : on le cherchait en bas, là
              où `PiedActions` le met partout ailleurs dans le produit. Posé
              `flex: none` hors de la zone de défilement, il reste visible quelle
              que soit la longueur de l'onglet.

              Il ne s'affiche QUE pendant une saisie : un pied permanent avec un
              bouton inerte apprend à ne plus le regarder. */}
          {enSaisie && (
            <div style={{ flex: 'none', padding: '12px 22px', borderTop: '1px solid var(--border-default)', background: 'var(--surface-card)' }}>
              <PiedActions taille="md"
                annuler={{ onClick: annulerOnglet, disabled: busy }}
                principale={{
                  /* ⚠️ « Enregistrer » TOUT COURT (RQ, 06/10/2026), et non
                     « Enregistrer contrat & administratif » : le pied est collé
                     SOUS l'onglet ouvert, il n'y a aucune ambiguïté sur ce qu'il
                     enregistre — et la convention §4 dit « Enregistrer ». Le
                     formulaire de contrat garde, lui, « Enregistrer le contrat » :
                     c'est un enregistrement à part, et les deux ne peuvent plus
                     s'ouvrir en même temps. */
                  libelle: 'Enregistrer',
                  libelleChargement: 'Enregistrement…',
                  chargement: busy,
                  disabled: busy || !!raisonBloquante,
                  title: raisonBloquante || undefined,
                  onClick: () => void enregistrerOnglet(),
                }} />
              {/* ⚠️ LA RAISON EST ÉCRITE, pas seulement en info-bulle : un bouton
                  grisé sans phrase se lit comme une panne — c'est le relevé du
                  menu de profil (05/10/2026), et il vaut ici aussi. */}
              {!!raisonBloquante && (
                <p style={{ margin: '7px 0 0', fontSize: 12, color: 'var(--red-600)', textAlign: 'end', lineHeight: 1.5 }}>
                  {raisonBloquante}
                </p>
              )}
            </div>
          )}
        </section>
      </div>

      {demande && (
        <Modale titre={demande.titre} onClose={() => { setDemande(null); setMotif(''); }} largeur={460}
          sousTitre="Rien n’est supprimé : documents, signatures et journaux gardent leur auteur."
          pied={<>
            <Bouton taille="sm" onClick={() => { setDemande(null); setMotif(''); }} disabled={busy}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" onClick={() => void majStatut()} disabled={busy}>
              <I n="check" s={12} />{busy ? 'Enregistrement…' : demande.action}
            </Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label htmlFor="motif-statut" style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
              {demande.question}
            </label>
            <textarea id="motif-statut" value={motif} rows={3} autoFocus
              onChange={(e) => setMotif(e.target.value)}
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            {/* Le champ vide est un choix, pas un oubli : on le DIT. */}
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Facultatif — laissé vide, le changement part quand même au journal, sans explication.
            </span>
          </div>
        </Modale>
      )}
    </>
  );
}
